/**
 * EntityEditorPage - transition admin for the generic entity catalog.
 *
 * The first exposed entities are Holding and Warranty. Attributes configured
 * here describe how the EAV surface should read native columns, workflow
 * states, and relationships while the platform moves fully onto entities.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Asterisk,
  Copy,
  Eye,
  GripVertical,
  Lock,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { DisplayPreviewDialog } from './display-preview-dialog'
import {
  STATUS_WORKFLOW_KEY,
  isResolvedDisplayValue,
} from './display-preview-helpers'
import { useHoldings } from '@/admin/hooks/use-products'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { buildRegistrationConfiguredFieldItems } from '@/admin/lib/registration-configured-fields'
import type { Holding } from '@/admin/types/products'
import {
  DEFAULT_ICON_KEY,
  ICON_MAP,
} from '@/admin/components/category-icons'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'
import { LucideIconPicker } from '@/shared/components/ui/lucide-icon-picker'
import {
  AttributeConfigurationField,
} from '@/admin/components/shared/attribute-configuration-field'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import {
  defaultValueFromConfig,
  hasDefaultValue,
  isChoiceAttributeFieldType,
  normaliseDefaultValue,
  optionValuesFromConfig,
  optionsToPayload,
  type AttributeDefaultValue,
  type AttributeOption,
} from '@/admin/components/shared/attribute-configuration-utils'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import {
  useCreateEntityAttribute,
  useCreateEntityType,
  useCreateEntityRelationship,
  useDeleteEntityAttribute,
  useDeleteEntityRelationship,
  useDeleteEntityType,
  useEntityTypes,
  useEntityRegistrationPreview,
  useReorderEntityAttributes,
  useResetEntityDisplayDefault,
  useUpdateEntityType,
  useUpdateEntityAttribute,
  useUpdateEntityAttributeAcrossEntities,
  useUpdateEntityRelationship,
  type EntityAttributeDefinition,
  type EntityAttributeSource,
  type EntityFieldType,
  type EntityTypeCreateInput,
  type EntityRegistrationConfig,

  type EntityRegistrationSegment,
  type EntityRelationshipDefinition,
  type EntityTableDisplayColumn,
  type EntityTypeDefinition,
  type EntityValueSource,
} from '@/admin/hooks/use-entities'
import {
  LENGTH_PATTERN_FIELD_TYPES,
  getDuplicateRuleRef,
  getValueRuleRef,
} from './registration-rule-refs'
import { useWorkflowDefinitions } from '@/admin/hooks/use-workflow-definitions'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Switch } from '@/shared/components/ui/switch'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateValidationRule,
  useDeleteValidationRule,
  useUpdateValidationRule,
  useValidationRules,
  type Severity,
  type ValidationRule,
} from '@/admin/hooks/use-validation-rules'
import {
  useAvailabilityRules,
  useCreateAvailabilityRule,
  useUpdateAvailabilityRule,
  useDeleteAvailabilityRule,
  type AvailabilityRule,
} from '@/admin/hooks/use-availability-rules'
import {
  useEntityActions,
  useCreateEntityAction,
  useUpdateEntityAction,
  useDeleteEntityAction,
  useReorderEntityActions,
  useEntityActionHandlers,
  type EntityAction,
  type ActionKind,
  type ActionVariant,
} from '@/admin/hooks/use-entity-actions'
import { useFeatureFlag, FLAGS } from '@/admin/hooks/use-feature-flag'
import { EntityRegistrationFormRenderer } from '@/admin/components/entity-registration/entity-registration-form-renderer'
import { ValidationRuleDialog } from '@/admin/pages/settings/validation-rule-dialog'
import {
  TemplateTokenInput,
  type TemplateTokenSuggestion,
} from '@/admin/components/shared/template-token-input'
import { buildEntityTreeRows } from '@/admin/lib/entity-tree'

const ENTITY_KEYS = ['host', 'property', 'structure', 'holding', 'warranty']

const FIELD_TYPE_OPTIONS: { value: EntityFieldType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'RICH_TEXT', label: 'Rich text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'SELECT', label: 'Select' },
  { value: 'MULTI_SELECT', label: 'Multi select' },
  { value: 'DATE', label: 'Date' },
  { value: 'DATE_TIME', label: 'Date & time' },
  { value: 'MONEY', label: 'Money' },
  { value: 'ADDRESS', label: 'Address' },
  { value: 'LINK', label: 'Link' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'ENTITY_SEARCH', label: 'Entity search' },
]

const SOURCE_OPTIONS: { value: EntityAttributeSource; label: string }[] = [
  { value: 'COLUMN', label: 'Column mirror' },
  { value: 'EAV', label: 'EAV attribute' },
  { value: 'COMPUTED', label: 'Computed value' },
]

const VALUE_SOURCE_OPTIONS: { value: EntityValueSource; label: string }[] = [
  { value: 'manual', label: 'Manual / stored value' },
  { value: 'registration_field', label: 'Registration field' },
  { value: 'workflow_state', label: 'Workflow state' },
  { value: 'entity_relationship', label: 'Related entity' },
]

// Value sources whose attribute row is a mirror of a source table (the claim
// schema, a warranty/product template). Their `required` is re-derived on every
// sync, so it is edited where it is authored, not here.
const PROJECTED_VALUE_SOURCES: ReadonlySet<string> = new Set([
  'registration_field',
  'product_attribute',
  'transaction_attribute',
  'warranty_attribute',
  'claim_attribute',
  'warranty_category',
])

const LOOKUP_MECHANISM_OPTIONS = [
  { value: 'address', label: 'Address' },
  { value: 'dvla', label: 'Vehicle details' },
]

const COMPUTED_TEMPLATE_RELATIONSHIP_DEPTH = 4

function lookupMechanismLabel(value: unknown) {
  const key = String(value ?? '')
  return LOOKUP_MECHANISM_OPTIONS.find((option) => option.value === key)?.label ?? key
}

const CARDINALITY_OPTIONS: {
  value: EntityRelationshipDefinition['cardinality']
  label: string
}[] = [
  { value: 'ONE', label: 'One-to-one / many-to-one' },
  { value: 'MANY', label: 'One-to-many' },
]

function toCodeName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

type VisibleWhenCondition = {
  field: string
  values: string[]
}

type EntityAttributeFormValues = {
  display_name: string
  field_type: EntityFieldType
  source: EntityAttributeSource
  column_name: string
  option_values: AttributeOption[]
  default_value: AttributeDefaultValue
  computed_default_template: string
  value_source: EntityValueSource
  workflow_key: string
  relationship_key: string
  editable: boolean
  required: boolean
  visible_when_enabled: boolean
  visible_when: VisibleWhenCondition
  required_when_visible: boolean
}

function parseVisibleWhen(
  configData: Record<string, unknown> | undefined,
): { enabled: boolean; condition: VisibleWhenCondition; requiredWhenVisible: boolean } {
  const vw = configData?.visible_when as Record<string, unknown> | undefined
  if (!vw || typeof vw !== 'object' || Array.isArray(vw)) {
    return { enabled: false, condition: { field: '', values: [] }, requiredWhenVisible: false }
  }
  const entries = Object.entries(vw)
  if (entries.length === 0) {
    return { enabled: false, condition: { field: '', values: [] }, requiredWhenVisible: false }
  }
  const [field, values] = entries[0]
  const rw = configData?.required_when as Record<string, unknown> | undefined
  const requiredWhenVisible = rw != null && typeof rw === 'object' && !Array.isArray(rw) && Object.keys(rw).length > 0
  return {
    enabled: true,
    condition: { field, values: Array.isArray(values) ? values : [] },
    requiredWhenVisible,
  }
}

type EntityRegistrationAttributeFormValues = {
  required: boolean
  lookup_enabled: boolean
  lookup_provider: string
  visibility_conditions: PredicateNode | null
}

type RegistrationRuleKind = 'duplicate' | 'min_length' | 'max_length' | 'pattern'

type RegistrationRuleDraft = {
  kind: RegistrationRuleKind
  severity: Severity
  message: string
  /** Length value for min_length / max_length kinds. */
  length: number
  /** Regex source for pattern kind. */
  pattern: string
}

type EntityRelationshipFormValues = {
  label: string
  target_entity_key: string
  cardinality: EntityRelationshipDefinition['cardinality']
}

type EntityTypeFormValues = {
  label: string
  description?: string
}

type DisplayColumnItem = {
  id: string
  entityKey: string
  attributeCodeName: string
  relationshipKey?: string
  relationshipPath?: string[]
  attribute: EntityAttributeDefinition
  groupLabel: string
  isFromRelationship: boolean
  isPrioritized?: boolean
  priorityRank?: number
}

export type DisplayColumnPriority = {
  codeName?: string
  entityKeys?: string[]
  sourceModel?: string
  sourceOwnerUuid?: string
  sourceAttributeUuid?: string
  hostTemplateSlug?: string
}

export type ExtraDisplayColumnItem = {
  id: string
  entityKey: string
  attributeCodeName: string
  relationshipKey?: string
  relationshipPath?: string[]
  displayName: string
  description?: string
  fieldType: EntityFieldType
  groupLabel?: string
  order?: number
}

export type AttributeDialogDefaults = {
  source?: EntityAttributeSource
}

/**
 * Editable subset of an attribute's ``configuration_data`` exposed by
 * the column edit dialog when the underlying source is COMPUTED. The
 * dialog only exposes the value template - every display-column
 * computed attribute uses ``value_source = 'manual'``, matching the
 * "+ Add computed field" creation flow.
 */
type ComputedConfigDraft = {
  default_value_template: string
}

/**
 * Shared rendering for the computed-attribute value-template input.
 *
 * Used in two places - the "+ Add computed field" creation form
 * (``EntityAttributeDialog`` / ``isDisplayComputedCreate`` mode) and
 * the per-column edit dialog (``DisplayColumnEditDialog``) - to keep
 * the visual treatment, label, helper copy, and ``TemplateTokenInput``
 * wiring perfectly in sync. If the input ever gains new affordances
 * (formatting, validation, suggestion ranking, etc.), changing this
 * component updates both surfaces at once.
 */
function ValueTemplateField({
  label,
  description,
  value,
  suggestions,
  onChange,
  placeholder,
}: {
  label: string
  description: ReactNode
  value: string
  suggestions: TemplateTokenSuggestion[]
  onChange: (next: string) => void
  placeholder: string
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </Label>
      <p className="text-[10px] text-[var(--ink-3)]">{description}</p>
      <TemplateTokenInput
        value={value}
        suggestions={suggestions}
        onChange={onChange}
        placeholder={placeholder}
        ariaLabel={label}
      />
    </div>
  )
}

type DisplayColumnEditResult = {
  /** Selected ``DisplayColumnItem.id`` after the user picked a source. */
  itemId: string
  /** Trimmed column header / card label. */
  label: string
  /**
   * When the user changed the source attribute's editable computed
   * configuration, the new ``configuration_data`` patch to apply
   * against the underlying ``EntityAttributeDefinition``. ``null`` when
   * the source is not COMPUTED or the config wasn't touched.
   */
  computed?: {
    entityKey: string
    attributeUuid: string
    configuration_data: Record<string, unknown>
  } | null
}

type EntityDisplaySectionKey =
  | 'registration_table'
  | 'registration_card'
  | 'purchase_table'
  | 'purchase_card'

export function EntityEditorPage() {
  const { data: entities = [], isLoading } = useEntityTypes()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [showCreateEntity, setShowCreateEntity] = useState(false)
  const visibleEntities = useMemo(
    () =>
      entities.filter(isEntityVisibleInEditor).sort(compareEntitiesForSidebar),
    [entities],
  )
  const selected =
    visibleEntities.find((entity) => entity.uuid === selectedUuid) ?? visibleEntities[0]

  return (
    <AppShell>
      <main className="page-container py-6">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h1 className="font-display text-[28px] text-[var(--ink)]">
              Entity Editor
            </h1>
            <p className="mt-1 max-w-3xl text-[12px] text-[var(--ink-3)]">
              Configure the organisation-owned entity schema used by workflows,
              rules, and operational displays.
            </p>
          </div>
        </div>

        <MasterDetail
          testId="entity-editor"
          title="Entities"
          onAdd={() => setShowCreateEntity(true)}
          hasSelection={!!selected}
          selectedId={selected?.uuid ?? null}
          visibleItemIds={visibleEntities.map((entity) => entity.uuid)}
          onSelectionChange={setSelectedUuid}
          onBack={() => setSelectedUuid(null)}
          list={
            isLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">Loading…</p>
            ) : (
              <EntityList
                entities={visibleEntities}
                selectedUuid={selected?.uuid ?? null}
                onSelect={setSelectedUuid}
              />
            )
          }
          detail={
            selected ? (
              <EntityDetail
                entity={selected}
                allEntities={entities}
                onSelectEntity={setSelectedUuid}
                onDelete={() => setSelectedUuid(null)}
              />
            ) : (
              <div className="flex justify-center pt-24">
                <p className="text-[13px] text-[var(--ink-3)]">
                  Select an entity to edit its attributes.
                </p>
              </div>
            )
          }
        />
        <EntityTypeDialog
          open={showCreateEntity}
          onOpenChange={setShowCreateEntity}
          onCreated={setSelectedUuid}
        />
      </main>
    </AppShell>
  )
}

function entitySortIndex(key: string) {
  const index = ENTITY_KEYS.indexOf(key)
  return index === -1 ? ENTITY_KEYS.length : index
}

function compareEntitiesForSidebar(a: EntityTypeDefinition, b: EntityTypeDefinition) {
  return entitySortIndex(a.key) - entitySortIndex(b.key) || a.label.localeCompare(b.label)
}

function isEntityVisibleInEditor(entity: EntityTypeDefinition) {
  return entity.metadata?.editor_hidden !== true
}

function canSelectRelationshipTarget(entity: EntityTypeDefinition) {
  if (entity.metadata?.editor_hidden !== true) return true
  return entity.metadata?.reference_only === true
}

function EntityList({
  entities,
  selectedUuid,
  onSelect,
}: {
  entities: EntityTypeDefinition[]
  selectedUuid: string | null
  onSelect: (uuid: string) => void
}) {
  if (entities.length === 0) {
    return (
      <p className="text-[12px] text-[var(--ink-3)]">
        No editable entities are available yet.
      </p>
    )
  }

  const rows = buildEntityTreeRows(entities, compareEntitiesForSidebar)

  return (
    <div className="flex flex-col gap-0.5">
      {rows.map(({ entity, depth }) => {
        const isActive = entity.uuid === selectedUuid
        const EntityIcon = ICON_MAP[entity.icon || DEFAULT_ICON_KEY] ?? ICON_MAP[DEFAULT_ICON_KEY]
        const isChild = depth > 0
        return (
          <div
            key={entity.uuid}
            className={cn(
              isChild && 'ml-4 border-l-[0.5px] border-l-[var(--rule)] pl-2',
            )}
          >
            <Button
              variant="ghost"
              onClick={() => onSelect(entity.uuid)}
              className={cn(
                'h-8 w-full justify-start gap-2 rounded-[4px] px-2 text-left text-[12px] transition-all duration-150',
                isActive
                  ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                  : 'bg-transparent text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]',
              )}
            >
              <EntityIcon size={13} className="shrink-0 text-[var(--ink-3)]" />
              <span className="min-w-0 flex-1 truncate">{entity.label}</span>
              {isChild ? (
                <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[9px]">
                  Child
                </Badge>
              ) : null}
              <span className="shrink-0 truncate font-mono text-[9px] text-[var(--ink-3)]">
                {entity.key}
              </span>
            </Button>
          </div>
        )
      })}
    </div>
  )
}

function EntityTypeDialog({
  open,
  onOpenChange,
  onCreated,
  parentEntity,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (uuid: string) => void
  parentEntity?: EntityTypeDefinition
}) {
  const create = useCreateEntityType()
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        placeholder: 'Property owner',
        value: '',
        required: 'Label is required',
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional description visible to other admins.',
        value: '',
      },
    ],
    [],
  )

  const { Form, ref } = useForm(
    {
      name: 'entity-type-create-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as EntityTypeFormValues
      const payload: EntityTypeCreateInput = {
        label: values.label.trim(),
        description: (values.description ?? '').trim(),
      }
      if (parentEntity) {
        payload.metadata = {
          parent_entity_key: parentEntity.key,
          entity_tier: 'child',
        }
      }
      try {
        const created = await create.mutateAsync(payload)
        toast.success(parentEntity ? 'Child entity created' : 'Entity created')
        onCreated(created.uuid)
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to create entity')
      }
    },
    [open, fields, parentEntity],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Create Entity</DialogTitle>
          <DialogDescription>
            {parentEntity
              ? `Add a child entity nested under ${parentEntity.label}. The key is generated from the label.`
              : 'Add an organisation-owned entity schema. The key is generated from the label.'}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-4 py-2" />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => ref.current?.submitForm()}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EntityDetail({
  entity,
  allEntities,
  onSelectEntity,
  onDelete,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  onSelectEntity: (uuid: string) => void
  onDelete: () => void
}) {
  const [editing, setEditing] = useState<EntityAttributeDefinition | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editingRelationship, setEditingRelationship] =
    useState<EntityRelationshipDefinition | null>(null)
  const [showCreateRelationship, setShowCreateRelationship] = useState(false)
  const [showCreateChild, setShowCreateChild] = useState(false)
  const [deletingAttribute, setDeletingAttribute] =
    useState<EntityAttributeDefinition | null>(null)
  const [deletingRelationship, setDeletingRelationship] =
    useState<EntityRelationshipDefinition | null>(null)
  const [createAttributeDefaults, setCreateAttributeDefaults] =
    useState<AttributeDialogDefaults | null>(null)
  const [showDeleteEntity, setShowDeleteEntity] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const remove = useDeleteEntityAttribute(entity.uuid)
  const removeRelationship = useDeleteEntityRelationship(entity.uuid)
  const removeEntity = useDeleteEntityType()
  const reorder = useReorderEntityAttributes(entity.uuid)
  const updateEntity = useUpdateEntityType()
  const addAttr = useCreateEntityAttribute(entity.uuid)
  const resourceLookupProviders = useResourceLookupProviders()
  const isCore = entity.is_registered || !!entity.django_model

  const attributes = useMemo(
    () =>
      [...(entity.attributes ?? [])].sort(
        (a, b) => a.order - b.order || a.display_name.localeCompare(b.display_name),
      ),
    [entity.attributes],
  )

  async function handleDeleteRelationship() {
    if (!deletingRelationship) return
    try {
      await removeRelationship.mutateAsync(deletingRelationship.uuid)
      toast.success('Entity relationship deleted')
      setDeletingRelationship(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete entity relationship')
    }
  }

  async function handleDeleteEntity() {
    try {
      await removeEntity.mutateAsync(entity.uuid)
      toast.success(`Entity "${entity.label}" deleted`)
      setShowDeleteEntity(false)
      onDelete()
    } catch (err) {
      toastApiError(err, 'Failed to delete entity')
    }
  }

  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setShowCreate(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await addAttr.mutateAsync({
          display_name: tpl.display_name,
          code_name: tpl.code_name,
          description: tpl.description ?? '',
          field_type: tpl.field_type as EntityFieldType,
          source: 'EAV',
          required: false,
          editable: true,
          configuration_data: tpl.configuration_data ?? {},
          order: attributes.length,
        })
        toast.success('Attribute added')
      } catch (err) {
        toastApiError(err, 'Failed to add attribute')
      }
    }
  }

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = attributes.findIndex((attr) => attr.uuid === active.id)
      const newIndex = attributes.findIndex((attr) => attr.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const ordered = arrayMove(attributes, oldIndex, newIndex).map((attr, index) => ({
        uuid: attr.uuid,
        order: index,
      }))
      reorder.mutate(ordered, {
        onError: (err) => toastApiError(err, 'Failed to reorder attributes'),
      })
    },
    [attributes, reorder],
  )

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2">
              {(() => {
                const EntityIcon = ICON_MAP[entity.icon || DEFAULT_ICON_KEY] ?? ICON_MAP[DEFAULT_ICON_KEY]
                return <EntityIcon size={16} className="text-[var(--ink-3)]" />
              })()}
              <h2 className="font-display text-[22px] text-[var(--ink)]">
                {entity.label}
              </h2>
              <Badge variant="outline" className="font-mono text-[10px]">
                {entity.key}
              </Badge>
            </div>
            <p className="max-w-2xl text-[12px] text-[var(--ink-3)]">
              {entity.description || 'Entity metadata and EAV schema.'}
            </p>
            {entity.django_model ? (
              <p className="font-mono text-[10px] text-[var(--ink-3)]">
                {entity.django_model}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <Tabs defaultValue="general" className="gap-4">
        <TabsList variant="line">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="registration">Registration</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                Icon
              </h3>
              <p className="text-[10px] text-[var(--ink-3)]">
                Choose an icon to represent this entity throughout the UI.
              </p>
            </div>
            <LucideIconPicker
              value={entity.icon || DEFAULT_ICON_KEY}
              onChange={(key) => {
                updateEntity.mutate(
                  { uuid: entity.uuid, icon: key || '' },
                  { onError: (err) => toastApiError(err, 'Failed to update entity icon') },
                )
              }}
              mode="floating"
            />
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                  Attributes
                </h3>
                <p className="text-[10px] text-[var(--ink-3)]">
                  Map Holding columns, workflow states, and EAV fields into one
                  entity schema.
                </p>
              </div>
              <AttributeTemplatePicker
                onPick={handleAddAttrPick}
                resetAfterPick
                asAddButton
                domain="entity"
                addButtonLabel="Add attribute"
                data-testid="entity-editor-add-attribute-picker"
              />
            </div>

            <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
              {attributes.length === 0 ? (
                <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                  No attributes yet.
                </div>
              ) : (
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={attributes.map((attribute) => attribute.uuid)}
                    strategy={verticalListSortingStrategy}
                  >
                    {attributes.map((attribute) => (
                      <SortableEntityAttributeRow
                        key={attribute.uuid}
                        attribute={attribute}
                        isFirst={attribute.uuid === attributes[0]?.uuid}
                        onEdit={() => setEditing(attribute)}
                        onDelete={() => setDeletingAttribute(attribute)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                  Relationships
                </h3>
                <p className="text-[10px] text-[var(--ink-3)]">
                  Relationship refs available to rules and workflow mappings.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setShowCreateRelationship(true)}
                >
                  <Plus size={11} className="mr-1" />
                  Add relationship
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setShowCreateChild(true)}
                >
                  <Plus size={11} className="mr-1" />
                  Add child
                </Button>
              </div>
            </div>
            <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
              {entity.relationships.length === 0 ? (
                <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                  No relationships yet.
                </div>
              ) : (
                entity.relationships.map((relationship) => (
                  <EntityRelationshipRow
                    key={relationship.uuid}
                    relationship={relationship}
                    onEdit={() => setEditingRelationship(relationship)}
                    onDelete={() => setDeletingRelationship(relationship)}
                  />
                ))
              )}
            </div>
          </section>

          {!isCore && (
            <section className="mt-4 rounded-[6px] border-[0.5px] border-red-200 bg-red-50/50 p-4 dark:border-red-900/40 dark:bg-red-950/20">
              <div className="flex items-start gap-3">
                <TriangleAlert size={16} className="mt-0.5 shrink-0 text-red-500" />
                <div className="flex flex-1 flex-col gap-2">
                  <div>
                    <h3 className="text-[12px] font-medium text-red-700 dark:text-red-400">
                      Danger zone
                    </h3>
                    <p className="mt-0.5 text-[11px] text-red-600/80 dark:text-red-400/70">
                      Deleting this entity permanently removes its schema definition,
                      all attributes, relationships, and any associated registration
                      configuration. This action cannot be undone.
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="destructive"
                      size="xs"
                      onClick={() => setShowDeleteEntity(true)}
                    >
                      <Trash2 size={11} className="mr-1" />
                      Delete entity
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </TabsContent>
        <TabsContent value="registration">
          <EntityRegistrationSection
            key={entity.uuid}
            entity={entity}
            allEntities={allEntities}
          />
        </TabsContent>
        <TabsContent value="display">
          <EntityDisplayTab
            key={`${entity.uuid}:${[
              ...(entity.metadata?.display?.registration_table?.columns ?? []),
              ...(entity.metadata?.display?.registration_card?.columns ?? []),
              ...(entity.metadata?.display?.purchase_table?.columns ?? []),
              ...(entity.metadata?.display?.purchase_card?.columns ?? []),
            ]
              .map((column) => `${column.id}:${column.label ?? ''}`)
              .join('|')}`}
            entity={entity}
            allEntities={allEntities}
            onAddComputedField={() => setCreateAttributeDefaults({ source: 'COMPUTED' })}
          />
        </TabsContent>
        <TabsContent value="actions">
          <EntityActionsTab
            entityUuid={entity.uuid}
            entityKey={entity.key}
            attributes={entity.attributes}
            relationships={entity.relationships}
          />
        </TabsContent>
      </Tabs>

      <EntityAttributeDialog
        entity={entity}
        allEntities={allEntities}
        attribute={undefined}
        open={showCreate || !!createAttributeDefaults}
        onOpenChange={(next) => {
          if (!next) {
            setShowCreate(false)
            setCreateAttributeDefaults(null)
            setPrefillName('')
          } else {
            setShowCreate(true)
          }
        }}
        defaultOrder={attributes.length}
        defaults={createAttributeDefaults ?? undefined}
        prefillName={prefillName}
      />
      <EntityAttributeDialog
        entity={entity}
        allEntities={allEntities}
        attribute={editing ?? undefined}
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
      />
      <EntityRelationshipDialog
        entity={entity}
        allEntities={allEntities}
        relationship={undefined}
        open={showCreateRelationship}
        onOpenChange={setShowCreateRelationship}
      />
      <EntityRelationshipDialog
        entity={entity}
        allEntities={allEntities}
        relationship={editingRelationship ?? undefined}
        open={!!editingRelationship}
        onOpenChange={(next) => {
          if (!next) setEditingRelationship(null)
        }}
      />
      <EntityTypeDialog
        open={showCreateChild}
        onOpenChange={setShowCreateChild}
        onCreated={onSelectEntity}
        parentEntity={entity}
      />
      <AttributeDeleteDialog
        open={!!deletingAttribute}
        onOpenChange={(next) => {
          if (!next) setDeletingAttribute(null)
        }}
        attributeName={deletingAttribute?.display_name}
        description="This removes the attribute from the entity schema. Existing operational displays and mappings that reference it may need to be reviewed."
        successMessage="Entity attribute deleted"
        errorFallback="Failed to delete entity attribute"
        isPending={remove.isPending}
        onDelete={({ force }) =>
          remove.mutateAsync({ attrUuid: deletingAttribute!.uuid, force })
        }
      />
      <ConfirmationDialog
        open={!!deletingRelationship}
        onOpenChange={(next) => {
          if (!next) setDeletingRelationship(null)
        }}
        title={`Delete ${deletingRelationship?.label ?? 'relationship'}?`}
        description="This removes the relationship reference from the entity schema. Rules and workflow mappings that reference it may need to be reviewed."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={removeRelationship.isPending}
        onConfirm={handleDeleteRelationship}
      />
      <ConfirmationDialog
        open={showDeleteEntity}
        onOpenChange={setShowDeleteEntity}
        title={`Delete "${entity.label}" entity?`}
        description={`This will permanently delete the "${entity.label}" entity, including all its attributes, relationships, and registration configuration. This action cannot be undone.`}
        confirmLabel="Delete entity"
        pendingLabel="Deleting..."
        destructive
        isPending={removeEntity.isPending}
        onConfirm={handleDeleteEntity}
      />
    </div>
    </ResourceLookupContextProvider>
  )
}

function isRegistrationAttribute(attribute: EntityAttributeDefinition) {
  return attribute.configuration_data?.value_source === 'registration_field'
}

function getRegistrationConfig(attribute: EntityAttributeDefinition): {
  visibility_conditions?: PredicateNode | null
} {
  const raw = attribute.configuration_data?.registration
  return raw && typeof raw === 'object' ? raw : {}
}

function buildDisplayColumnItems(
  entity: EntityTypeDefinition,
  allEntities: EntityTypeDefinition[],
  priorities: DisplayColumnPriority[] = [],
  extraItems: ExtraDisplayColumnItem[] = [],
): DisplayColumnItem[] {
  const byKey = new Map(allEntities.map((candidate) => [candidate.key, candidate]))
  const items: DisplayColumnItem[] = entity.attributes.map((attribute) => {
    const priorityRank = displayPriorityRank(
      {
        entityKey: entity.key,
        attribute,
        isFromRelationship: false,
      },
      priorities,
    )
    return {
      id: `entity:${entity.key}:${attribute.code_name}`,
      entityKey: entity.key,
      attributeCodeName: attribute.code_name,
      attribute,
      groupLabel: entity.label,
      isFromRelationship: false,
      isPrioritized: priorityRank !== undefined,
      priorityRank,
    }
  })

  const addRelationshipItems = (
    currentEntity: EntityTypeDefinition,
    relationshipPath: string[],
    labelPath: string[],
    visitedEntityKeys: Set<string>,
  ) => {
    for (const relationship of currentEntity.relationships) {
      const target = byKey.get(relationship.target_key)
      if (!target || visitedEntityKeys.has(target.key)) continue

      const nextRelationshipPath = [...relationshipPath, relationship.key]
      const nextLabelPath = [...labelPath, relationship.label]
      const relationshipPathId = nextRelationshipPath.join('.')

      items.push(
        ...target.attributes.map((attribute) => {
          const priorityRank = displayPriorityRank(
            {
              entityKey: target.key,
              attribute,
              isFromRelationship: true,
            },
            priorities,
          )
          return {
            id: `relationship:${relationshipPathId}:${target.key}:${attribute.code_name}`,
            entityKey: target.key,
            attributeCodeName: attribute.code_name,
            relationshipKey: nextRelationshipPath[0],
            relationshipPath: nextRelationshipPath,
            attribute,
            groupLabel: `${entity.label} → ${nextLabelPath.join(' → ')}`,
            isFromRelationship: true,
            isPrioritized: priorityRank !== undefined,
            priorityRank,
          }
        }),
      )

      addRelationshipItems(target, nextRelationshipPath, nextLabelPath, new Set([
        ...visitedEntityKeys,
        target.key,
      ]))
    }
  }

  addRelationshipItems(entity, [], [], new Set([entity.key]))

  items.push(...extraItems.map((extra): DisplayColumnItem => ({
    id: extra.id,
    entityKey: extra.entityKey,
    attributeCodeName: extra.attributeCodeName,
    relationshipKey: extra.relationshipKey,
    relationshipPath: extra.relationshipPath,
    attribute: {
      uuid: extra.id,
      code_name: extra.attributeCodeName,
      display_name: extra.displayName,
      description: extra.description ?? '',
      field_type: extra.fieldType,
      source: 'EAV',
      column_name: '',
      required: false,
      editable: true,
      configuration_data: { value_source: 'transaction_attribute' },
      hint: '',
      order: extra.order ?? 0,
    },
    groupLabel: extra.groupLabel ?? 'Template fields',
    isFromRelationship: Boolean(extra.relationshipPath?.length || extra.relationshipKey),
    isPrioritized: true,
    priorityRank: 0,
  })))

  return items.sort(compareDisplayColumnItems)
}

function displayPriorityRank(
  item: {
    entityKey: string
    attribute: EntityAttributeDefinition
    isFromRelationship: boolean
  },
  priorities: DisplayColumnPriority[],
) {
  const config = item.attribute.configuration_data ?? {}
  const priorityIndex = priorities.findIndex((priority) => {
    const entityMatches = !priority.entityKeys || priority.entityKeys.includes(item.entityKey)
    const codeMatches = !priority.codeName || priority.codeName === item.attribute.code_name
    if (!entityMatches || !codeMatches) return false

    if (
      priority.sourceAttributeUuid &&
      config.source_attribute_uuid === priority.sourceAttributeUuid
    ) {
      return true
    }

    if (
      priority.sourceOwnerUuid &&
      config.source_owner_uuid === priority.sourceOwnerUuid &&
      (!priority.sourceModel || config.source_model === priority.sourceModel)
    ) {
      return true
    }

    if (
      priority.hostTemplateSlug &&
      config.host_template_slug === priority.hostTemplateSlug
    ) {
      return true
    }

    return Boolean(priority.codeName && priority.entityKeys)
  })
  return priorityIndex === -1 ? undefined : priorityIndex
}

function displayColumnItemRank(item: DisplayColumnItem) {
  if (item.isPrioritized) return 0
  const isRegistrationField = isRegistrationAttribute(item.attribute)
  if (isRegistrationField && !item.isFromRelationship) return 1
  if (isRegistrationField) return 2
  if (!item.isFromRelationship) return 3
  return 4
}

function compareDisplayColumnItems(a: DisplayColumnItem, b: DisplayColumnItem) {
  const aPriorityRank = a.priorityRank ?? Number.MAX_SAFE_INTEGER
  const bPriorityRank = b.priorityRank ?? Number.MAX_SAFE_INTEGER

  return (
    aPriorityRank - bPriorityRank ||
    displayColumnItemRank(a) - displayColumnItemRank(b) ||
    a.groupLabel.localeCompare(b.groupLabel) ||
    a.attribute.order - b.attribute.order ||
    a.attribute.display_name.localeCompare(b.attribute.display_name)
  )
}

function displayColumnPickerGroupLabel(item: DisplayColumnItem) {
  if (item.isPrioritized) return 'Template fields'
  return displayColumnItemRank(item) <= 1 ? 'Registration fields' : 'Other fields'
}

const DEFAULT_RULE_DRAFT: RegistrationRuleDraft = {
  kind: 'duplicate',
  severity: 'WARN',
  message: '',
  length: 1,
  pattern: '',
}

function duplicateRuleCondition(ref: string): PredicateNode {
  return { type: 'clause', ref, op: 'already_exists' }
}

function minLengthRuleCondition(ref: string, length: number): PredicateNode {
  const max = Math.max(0, length - 1)
  return { type: 'clause', ref, op: 'matches', value: `^[\\s\\S]{0,${max}}$` }
}

function maxLengthRuleCondition(ref: string, length: number): PredicateNode {
  const min = length + 1
  return { type: 'clause', ref, op: 'matches', value: `^[\\s\\S]{${min},}$` }
}

function patternRuleCondition(ref: string, pattern: string): PredicateNode {
  return { type: 'clause', ref, op: 'not_matches', value: pattern }
}

function buildRuleCondition(
  draft: RegistrationRuleDraft,
  refs: { duplicateRef: string | null; valueRef: string | null },
): PredicateNode | null {
  switch (draft.kind) {
    case 'duplicate':
      return refs.duplicateRef ? duplicateRuleCondition(refs.duplicateRef) : null
    case 'min_length':
      return refs.valueRef ? minLengthRuleCondition(refs.valueRef, draft.length) : null
    case 'max_length':
      return refs.valueRef ? maxLengthRuleCondition(refs.valueRef, draft.length) : null
    case 'pattern':
      return refs.valueRef && draft.pattern.trim()
        ? patternRuleCondition(refs.valueRef, draft.pattern.trim())
        : null
  }
}

const MIN_LENGTH_RE = /^\^\[\\s\\S\]\{0,(\d+)\}\$$/
const MAX_LENGTH_RE = /^\^\[\\s\\S\]\{(\d+),\}\$$/

function clauseFromConditions(conditions: PredicateNode | null) {
  if (!conditions) return null
  if (conditions.type === 'clause') return conditions
  const clauseChild = conditions.children.find((c) => c.type === 'clause')
  return clauseChild?.type === 'clause' ? clauseChild : null
}

/**
 * Detect whether a stored rule belongs to one of the known kinds for
 * this attribute. Returns the kind + the human-meaningful value (length
 * for length kinds, regex for pattern). Rules that don't fit any of the
 * known shapes return null and are silently ignored - they still live
 * in the engine, they just don't surface in this minimal UI.
 */
function detectRegistrationRule(
  rule: ValidationRule,
  refs: { duplicateRef: string | null; valueRef: string | null },
): { kind: RegistrationRuleKind; length?: number; pattern?: string } | null {
  const clause = clauseFromConditions(rule.conditions)
  if (!clause) return null
  const duplicateRefs = refs.duplicateRef
    ? refs.duplicateRef === 'entity:host.address'
      ? [refs.duplicateRef, 'entity:host.address_line_1', 'entity:host.address_postcode']
      : [refs.duplicateRef]
    : []
  if (
    clause.op === 'already_exists' &&
    duplicateRefs.includes(clause.ref)
  ) {
    return { kind: 'duplicate' }
  }
  if (refs.valueRef && clause.ref === refs.valueRef) {
    if (clause.op === 'matches') {
      const value = String(clause.value ?? '')
      const minMatch = value.match(MIN_LENGTH_RE)
      if (minMatch) {
        const cap = Number(minMatch[1])
        if (Number.isFinite(cap)) return { kind: 'min_length', length: cap + 1 }
      }
      const maxMatch = value.match(MAX_LENGTH_RE)
      if (maxMatch) {
        const cap = Number(maxMatch[1])
        if (Number.isFinite(cap)) return { kind: 'max_length', length: cap - 1 }
      }
    }
    if (clause.op === 'not_matches') {
      return { kind: 'pattern', pattern: String(clause.value ?? '') }
    }
  }
  return null
}

function defaultMessageForKind(
  kind: RegistrationRuleKind,
  attribute: EntityAttributeDefinition,
  detail?: { length?: number; pattern?: string },
) {
  const fieldLabel = attribute.field_type === 'ADDRESS' ? 'address' : attribute.display_name
  switch (kind) {
    case 'duplicate':
      return `A registration already exists with this ${fieldLabel.toLowerCase()}.`
    case 'min_length':
      return `${fieldLabel} must be at least ${detail?.length ?? 1} characters.`
    case 'max_length':
      return `${fieldLabel} must be at most ${detail?.length ?? 1} characters.`
    case 'pattern':
      return `${fieldLabel} format is invalid.`
  }
}

function nameForKind(kind: RegistrationRuleKind, attribute: EntityAttributeDefinition) {
  switch (kind) {
    case 'duplicate':
      return `Duplicate ${attribute.display_name}`
    case 'min_length':
      return `Min length ${attribute.display_name}`
    case 'max_length':
      return `Max length ${attribute.display_name}`
    case 'pattern':
      return `Pattern ${attribute.display_name}`
  }
}

function labelForKind(kind: RegistrationRuleKind) {
  switch (kind) {
    case 'duplicate':
      return 'Duplicate check'
    case 'min_length':
      return 'Min length'
    case 'max_length':
      return 'Max length'
    case 'pattern':
      return 'Pattern'
  }
}

function summaryForKind(
  kind: RegistrationRuleKind,
  detail: { length?: number; pattern?: string } = {},
) {
  switch (kind) {
    case 'duplicate':
      return 'Value already exists'
    case 'min_length':
      return `At least ${detail.length ?? 0} characters`
    case 'max_length':
      return `At most ${detail.length ?? 0} characters`
    case 'pattern':
      return detail.pattern ? `Matches /${detail.pattern}/` : 'Matches pattern'
  }
}

function iconForKind(kind: RegistrationRuleKind) {
  switch (kind) {
    case 'duplicate':
      return Copy
    case 'min_length':
      return ArrowDownToLine
    case 'max_length':
      return ArrowUpToLine
    case 'pattern':
      return Asterisk
  }
}

function isValidRegex(source: string): boolean {
  if (!source.trim()) return false
  try {
    new RegExp(source)
    return true
  } catch {
    return false
  }
}

function SortableDisplayColumnRow({
  item,
  label,
  onEdit,
  onRemove,
  isFirst,
}: {
  item: DisplayColumnItem
  label: string
  onEdit: () => void
  onRemove: () => void
  isFirst: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 bg-[var(--bg)] px-3 py-2.5',
        !isFirst && 'border-t-[0.5px] border-t-[var(--rule)]',
        isDragging && 'opacity-50',
      )}
    >
      <div
        className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] text-[var(--ink)]">
            {label}
          </span>
          {label !== item.attribute.display_name ? (
            <Badge variant="ghost" className="text-[10px]">
              {item.attribute.display_name}
            </Badge>
          ) : null}
          <Badge variant="outline" className="font-mono text-[10px]">
            {item.attribute.code_name}
          </Badge>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.08em]">
            {item.attribute.field_type.toLowerCase().replace(/_/g, ' ')}
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
          {item.attribute.description ||
            (item.isFromRelationship ? 'Related entity attribute.' : 'Entity attribute.')}
        </p>
      </div>
      <Badge
        variant={item.isFromRelationship ? 'outline' : 'ghost'}
        className="shrink-0 text-[10px]"
      >
        {item.groupLabel}
      </Badge>
      <Button type="button" variant="ghost" size="icon-xs" onClick={onEdit}>
        <Pencil size={13} />
      </Button>
      <Button type="button" variant="ghost" size="icon-xs" onClick={onRemove}>
        <X size={13} />
      </Button>
    </div>
  )
}

function DisplayColumnEditDialog({
  item,
  label,
  allDisplayItems,
  allEntities,
  title,
  description,
  fieldLabel,
  open,
  onOpenChange,
  onSave,
}: {
  item: DisplayColumnItem | null
  label: string
  /** Pool of remap targets - same list rendered in the "+ Add column" picker. */
  allDisplayItems: DisplayColumnItem[]
  /** Used to resolve the owner entity of the selected attribute for relationship suggestions and template tokens. */
  allEntities: EntityTypeDefinition[]
  title: string
  description: string
  fieldLabel: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (result: DisplayColumnEditResult) => void
}) {
  // Local state so the dialog can swap source attribute + edit
  // computed config without committing until the user clicks Save.
  // Reset-on-(re)open is done via the `set state during render` pattern
  // (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // rather than an effect, which would otherwise trigger the
  // `react-hooks/set-state-in-effect` lint and a wasted render.
  const [labelDraft, setLabelDraft] = useState(label)
  const [selectedItemId, setSelectedItemId] = useState<string>(item?.id ?? '')
  const [openSnapshot, setOpenSnapshot] = useState({
    open,
    itemId: item?.id ?? '',
  })
  if (
    openSnapshot.open !== open
    || (open && openSnapshot.itemId !== (item?.id ?? ''))
  ) {
    setOpenSnapshot({ open, itemId: item?.id ?? '' })
    if (open) {
      setLabelDraft(label)
      setSelectedItemId(item?.id ?? '')
    }
  }

  const itemById = useMemo(
    () => new Map(allDisplayItems.map((candidate) => [candidate.id, candidate])),
    [allDisplayItems],
  )
  const selectedItem = selectedItemId
    ? itemById.get(selectedItemId) ?? item
    : item

  const sourceGroups = useMemo<SearchableSelectGroup[]>(() => {
    const groups = new Map<string, DisplayColumnItem[]>()
    for (const candidate of allDisplayItems) {
      const groupLabel = displayColumnPickerGroupLabel(candidate)
      const existing = groups.get(groupLabel) ?? []
      existing.push(candidate)
      groups.set(groupLabel, existing)
    }
    return [...groups.entries()].map(([groupLabel, candidates]) => ({
      label: groupLabel,
      options: candidates.map((candidate) => ({
        value: candidate.id,
        label: candidate.attribute.display_name,
        searchText: [
          groupLabel,
          candidate.groupLabel,
          candidate.entityKey,
          candidate.attribute.display_name,
          candidate.attribute.code_name,
          candidate.attribute.description,
          candidate.attribute.field_type,
          // Full dot-path as rendered in the subtitle (see availableColumnGroups).
          // Only when a relationship path exists - otherwise the join equals
          // ``code_name``, which is already in the haystack above.
          candidate.relationshipPath?.length
            ? [...candidate.relationshipPath, candidate.attribute.code_name].join('.')
            : null,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    }))
  }, [allDisplayItems])

  // Computed-config draft initialised from the currently-selected
  // attribute's ``configuration_data``. Re-seeded whenever the source
  // changes so the editor reflects the new attribute's current state.
  const isComputed = selectedItem?.attribute.source === 'COMPUTED'
  const initialComputed = useMemo<ComputedConfigDraft>(() => {
    const config = selectedItem?.attribute.configuration_data ?? {}
    return {
      default_value_template:
        typeof config.default_value === 'string' ? config.default_value : '',
    }
  }, [selectedItem])
  // Same `set state during render` pattern: when the user picks a
  // different source attribute, we want the computed editor to reflect
  // that attribute's saved configuration_data. Tracking the previously
  // observed selectedItem.id avoids the setState-in-effect lint.
  const [computedDraft, setComputedDraft] = useState<ComputedConfigDraft>(initialComputed)
  const [computedSourceId, setComputedSourceId] = useState<string>(
    selectedItem?.id ?? '',
  )
  if (computedSourceId !== (selectedItem?.id ?? '')) {
    setComputedSourceId(selectedItem?.id ?? '')
    setComputedDraft(initialComputed)
  }

  const computedDirty = useMemo(() => {
    if (!isComputed) return false
    return (
      computedDraft.default_value_template !== initialComputed.default_value_template
    )
  }, [computedDraft, initialComputed, isComputed])

  // The owner entity is the EntityType the selected attribute is defined
  // on. We use it to seed template-token suggestions, mirroring how
  // ``EntityAttributeDialog`` builds those for new computed attributes.
  const ownerEntity = useMemo(
    () =>
      selectedItem
        ? allEntities.find((candidate) => candidate.key === selectedItem.entityKey)
        : undefined,
    [allEntities, selectedItem],
  )

  const computedTemplateSuggestions = useMemo(
    () =>
      ownerEntity
        ? buildComputedTemplateSuggestions(
            ownerEntity,
            allEntities,
            selectedItem?.attribute.uuid,
          )
        : [],
    [allEntities, ownerEntity, selectedItem?.attribute.uuid],
  )

  const handleSubmit = () => {
    if (!selectedItem) return
    const trimmedLabel = labelDraft.trim()
    if (!trimmedLabel) return

    const computedPatch =
      isComputed && computedDirty
        ? buildComputedConfigurationPatch(selectedItem, computedDraft)
        : null

    onSave({
      itemId: selectedItem.id,
      label: trimmedLabel,
      computed: computedPatch,
    })
    onOpenChange(false)
  }

  const canSave = Boolean(selectedItem) && labelDraft.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {fieldLabel}
            </Label>
            <Input
              value={labelDraft}
              onChange={(event) => setLabelDraft(event.target.value)}
              placeholder={selectedItem?.attribute.display_name ?? fieldLabel}
            />
          </div>

          {isComputed && selectedItem ? (
            <ValueTemplateField
              label="Value template"
              description={
                <>
                  Saved against{' '}
                  <span className="font-mono">
                    {selectedItem.entityKey}.{selectedItem.attribute.code_name}
                  </span>
                  . Type fixed text, or start typing an attribute name
                  to insert a token. Suggestions appear when the typed
                  text matches.
                </>
              }
              value={computedDraft.default_value_template}
              suggestions={computedTemplateSuggestions}
              onChange={(next) =>
                setComputedDraft((current) => ({
                  ...current,
                  default_value_template: next,
                }))
              }
              placeholder="e.g. Registration consumer.email"
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Source attribute
              </Label>
              <p className="text-[10px] text-[var(--ink-3)]">
                Remap this column to a different attribute. The new
                value will be resolved via the same path as the picker
                above.
              </p>
              <SearchableSelect
                value={selectedItemId}
                onValueChange={(next) => setSelectedItemId(next)}
                groups={sourceGroups}
                placeholder="Select attribute"
                searchPlaceholder="Search attributes..."
                emptyMessage="No matching attributes"
                triggerClassName="h-8 text-[12px]"
                contentClassName="w-[460px]"
                renderOption={(option) => {
                  const candidate = itemById.get(option.value)
                  if (!candidate) return
                  return (
                    <div className="min-w-0">
                      <div className="truncate text-[13px]">
                        {candidate.attribute.display_name}
                      </div>
                      <div className="mt-0.5 whitespace-normal break-words font-mono text-[10px] text-[var(--ink-3)]">
                        {[...(candidate.relationshipPath ?? []), candidate.attribute.code_name].join(
                          '.',
                        )}
                      </div>
                    </div>
                  )
                }}
              />
              {selectedItem ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {selectedItem.attribute.code_name}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase tracking-[0.08em]"
                  >
                    {selectedItem.attribute.field_type
                      .toLowerCase()
                      .replace(/_/g, ' ')}
                  </Badge>
                  <Badge
                    variant={selectedItem.isFromRelationship ? 'outline' : 'ghost'}
                    className="text-[10px]"
                  >
                    {selectedItem.groupLabel}
                  </Badge>
                </div>
              ) : null}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSave} onClick={handleSubmit}>
            Save column
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Build the ``configuration_data`` patch sent to the backend when a
 * computed attribute's edit form is submitted. We merge with the
 * existing config (rather than replacing) so bespoke seed-defined keys
 * - e.g. ``fallbacks`` on OC's ``warranty.name`` - survive the edit.
 */
function buildComputedConfigurationPatch(
  item: DisplayColumnItem,
  draft: ComputedConfigDraft,
): DisplayColumnEditResult['computed'] {
  const existing = (item.attribute.configuration_data ?? {}) as Record<string, unknown>
  const next: Record<string, unknown> = { ...existing }

  // Only set value_source = 'manual' if no custom resolver is already
  // wired up (e.g. 'transaction_attribute' or any seed-declared
  // resolver). Custom resolvers stay intact so the rendered value
  // continues to match the seed contract.
  if (!next.value_source || next.value_source === 'manual') {
    next.value_source = 'manual'
  }

  if (draft.default_value_template.trim()) {
    next.default_value = draft.default_value_template
  } else {
    delete next.default_value
  }

  return {
    entityKey: item.entityKey,
    attributeUuid: item.attribute.uuid,
    configuration_data: next,
  }
}

export function EntityDisplayTab({
  entity,
  allEntities,
  onAddComputedField,
  sections = ['registration_table', 'registration_card'],
  priorityFields = [],
  extraDisplayItems = [],
  cardDisplayLabels,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  onAddComputedField?: () => void
  sections?: EntityDisplaySectionKey[]
  priorityFields?: DisplayColumnPriority[]
  extraDisplayItems?: ExtraDisplayColumnItem[]
  cardDisplayLabels?: Partial<{
    description: string
    labelDialogDescription: string
  }>
}) {
  const allDisplayItems = useMemo(
    () => buildDisplayColumnItems(entity, allEntities, priorityFields, extraDisplayItems),
    [entity, allEntities, priorityFields, extraDisplayItems],
  )

  return (
    <section className="flex flex-col gap-6">
      {onAddComputedField ? (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="xs" onClick={onAddComputedField}>
            <Plus size={11} className="mr-1" />
            Add computed field
          </Button>
        </div>
      ) : null}
      {sections.includes('registration_table') ? (
        <EntityDisplaySettingsCard
          key={`table:${displayColumnsFor(entity, 'registration_table')
            .map((column) => `${column.id}:${column.label ?? ''}`)
            .join('|')}`}
          entity={entity}
          allEntities={allEntities}
          allDisplayItems={allDisplayItems}
          sectionKey="registration_table"
          title="Table display"
          description="Add columns, then drag them into the order they should appear on the registration table."
          addPlaceholder="Add column"
          allSelectedPlaceholder="All columns added"
          searchPlaceholder="Search columns..."
          emptyMessage="No columns selected. Use Add column to choose attributes for the table."
          labelDialogTitle="Edit column header"
          labelDialogDescription="This label will be used as the registration table header."
          labelFieldLabel="Column header"
        />
      ) : null}
      {sections.includes('registration_card') ? (
        <EntityDisplaySettingsCard
          key={`card:${displayColumnsFor(entity, 'registration_card')
            .map((column) => `${column.id}:${column.label ?? ''}`)
            .join('|')}`}
          entity={entity}
          allEntities={allEntities}
          allDisplayItems={allDisplayItems}
          sectionKey="registration_card"
          title="Card display"
          description={
            cardDisplayLabels?.description ??
            'Add fields, then drag them into the order they should appear on registration cards.'
          }
          addPlaceholder="Add field"
          allSelectedPlaceholder="All fields added"
          searchPlaceholder="Search fields..."
          emptyMessage="No fields selected. Use Add field to choose attributes for the card."
          labelDialogTitle="Edit card label"
          labelDialogDescription={
            cardDisplayLabels?.labelDialogDescription ??
            'This label will be used on registration cards.'
          }
          labelFieldLabel="Card label"
        />
      ) : null}
      {sections.includes('purchase_table') ? (
        <EntityDisplaySettingsCard
          key={`purchase-table:${displayColumnsFor(entity, 'purchase_table')
            .map((column) => `${column.id}:${column.label ?? ''}`)
            .join('|')}`}
          entity={entity}
          allEntities={allEntities}
          allDisplayItems={allDisplayItems}
          sectionKey="purchase_table"
          title="Table display"
          description="Add columns, then drag them into the order they should appear on the transactions table."
          addPlaceholder="Add column"
          allSelectedPlaceholder="All columns added"
          searchPlaceholder="Search columns..."
          emptyMessage="No columns selected. Use Add column to choose attributes for the table."
          labelDialogTitle="Edit column header"
          labelDialogDescription="This label will be used as the transactions table header."
          labelFieldLabel="Column header"
        />
      ) : null}
      {sections.includes('purchase_card') ? (
        <EntityDisplaySettingsCard
          key={`purchase:${displayColumnsFor(entity, 'purchase_card')
            .map((column) => `${column.id}:${column.label ?? ''}`)
            .join('|')}`}
          entity={entity}
          allEntities={allEntities}
          allDisplayItems={allDisplayItems}
          sectionKey="purchase_card"
          title="Card display"
          description="Add fields, then drag them into the order they should appear on purchase / transaction cards."
          addPlaceholder="Add field"
          allSelectedPlaceholder="All fields added"
          searchPlaceholder="Search fields..."
          emptyMessage="No fields selected. Use Add field to choose attributes for the card."
          labelDialogTitle="Edit card label"
          labelDialogDescription="This label will be used on transaction cards."
          labelFieldLabel="Card label"
        />
      ) : null}
    </section>
  )
}

function displayColumnsFor(
  entity: EntityTypeDefinition,
  sectionKey: EntityDisplaySectionKey,
): EntityTableDisplayColumn[] {
  return entity.metadata?.display?.[sectionKey]?.columns ?? []
}

function toDisplayColumnPayload(
  item: DisplayColumnItem,
  label: string,
): EntityTableDisplayColumn {
  return {
    id: item.id,
    entity_key: item.entityKey,
    attribute_code_name: item.attributeCodeName,
    label,
    ...(item.relationshipKey ? { relationship_key: item.relationshipKey } : {}),
    ...(item.relationshipPath?.length ? { relationship_path: item.relationshipPath } : {}),
  }
}

function EntityDisplaySettingsCard({
  entity,
  allEntities,
  allDisplayItems,
  sectionKey,
  title,
  description,
  addPlaceholder,
  allSelectedPlaceholder,
  searchPlaceholder,
  emptyMessage,
  labelDialogTitle,
  labelDialogDescription,
  labelFieldLabel,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  allDisplayItems: DisplayColumnItem[]
  sectionKey: EntityDisplaySectionKey
  title: string
  description: string
  addPlaceholder: string
  allSelectedPlaceholder: string
  searchPlaceholder: string
  emptyMessage: string
  labelDialogTitle: string
  labelDialogDescription: string
  labelFieldLabel: string
}) {
  const updateEntity = useUpdateEntityType()
  const resetDisplay = useResetEntityDisplayDefault()
  const updateAttributeAcrossEntities = useUpdateEntityAttributeAcrossEntities()
  const initialItems = useMemo(
    () => {
      const savedColumns = displayColumnsFor(entity, sectionKey)
      if (savedColumns.length > 0) {
        const itemById = new Map(allDisplayItems.map((item) => [item.id, item]))
        return savedColumns
          .map((column) => itemById.get(column.id))
          .filter((item): item is DisplayColumnItem => Boolean(item))
      }

      return []
    },
    [allDisplayItems, entity, sectionKey],
  )
  const initialLabels = useMemo(() => {
    const savedColumns = displayColumnsFor(entity, sectionKey)
    const savedLabels = new Map(savedColumns.map((column) => [column.id, column.label]))
    return Object.fromEntries(
      initialItems.map((item) => [
        item.id,
        savedLabels.get(item.id) || item.attribute.display_name,
      ]),
    )
  }, [entity, initialItems, sectionKey])
  const [items, setItems] = useState<DisplayColumnItem[]>(initialItems)
  const [labels, setLabels] = useState<Record<string, string>>(initialLabels)
  const [editingLabelItem, setEditingLabelItem] = useState<DisplayColumnItem | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const { locale, currency } = useFormatting()
  // Sample of recent registrations used both to decide whether a preview
  // would be meaningful and to render it. Shared (deduped) across every
  // display-settings card on the tab.
  const { data: holdingsData } = useHoldings({ page_size: '10' })
  const sampleHoldings = useMemo(
    () => (holdingsData?.results ?? []) as Holding[],
    [holdingsData],
  )
  const selectedIds = useMemo(() => new Set(items.map((item) => item.id)), [items])
  const availableItems = useMemo(
    () => allDisplayItems.filter((item) => !selectedIds.has(item.id)),
    [allDisplayItems, selectedIds],
  )
  const availableItemById = useMemo(
    () => new Map(availableItems.map((item) => [item.id, item])),
    [availableItems],
  )
  const availableColumnGroups = useMemo<SearchableSelectGroup[]>(() => {
    const groups = new Map<string, DisplayColumnItem[]>()
    for (const item of availableItems) {
      const groupLabel = displayColumnPickerGroupLabel(item)
      const group = groups.get(groupLabel) ?? []
      group.push(item)
      groups.set(groupLabel, group)
    }

    return [...groups.entries()].map(([groupLabel, groupItems]) => ({
      label: groupLabel,
      options: groupItems.map((item) => ({
        value: item.id,
        label: item.attribute.display_name,
        searchText: [
          groupLabel,
          item.groupLabel,
          item.entityKey,
          item.attribute.display_name,
          item.attribute.code_name,
          item.attribute.description,
          item.attribute.field_type,
          // The full dot-path exactly as rendered in the subtitle, so
          // typing what you see (e.g. ``holdings.warranties.status``) matches.
          // Only when a relationship path exists - otherwise the join equals
          // ``code_name``, which is already in the haystack above.
          item.relationshipPath?.length
            ? [...item.relationshipPath, item.attribute.code_name].join('.')
            : null,
        ]
          .filter(Boolean)
          .join(' '),
      })),
    }))
  }, [availableItems])

  const getColumnLabel = useCallback(
    (item: DisplayColumnItem) => labels[item.id] || item.attribute.display_name,
    [labels],
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setItems((current) => {
      const oldIndex = current.findIndex((item) => item.id === String(active.id))
      const newIndex = current.findIndex((item) => item.id === String(over.id))
      if (oldIndex === -1 || newIndex === -1) return current
      return arrayMove(current, oldIndex, newIndex)
    })
  }, [])

  const handleSave = useCallback(async () => {
    try {
      await updateEntity.mutateAsync({
        uuid: entity.uuid,
        metadata: {
          ...(entity.metadata ?? {}),
          display: {
            ...(entity.metadata?.display ?? {}),
            [sectionKey]: {
              ...(entity.metadata?.display?.[sectionKey] ?? {}),
              columns: items.map((item) => toDisplayColumnPayload(item, getColumnLabel(item))),
            },
          },
        },
      })
      toast.success(`${title} settings saved`)
    } catch (err) {
      toastApiError(err, `Failed to save ${title.toLowerCase()} settings`)
    }
  }, [entity, getColumnLabel, items, sectionKey, title, updateEntity])

  // Reset to default: restore this section's curated platform-default
  // columns (seeded by the backend) and persist immediately. Only offered
  // when the entity actually has a default for this section. On success the
  // entity caches invalidate and this card remounts (its key is derived from
  // the saved columns), so the working list is replaced by the defaults.
  const canReset = (entity.default_display_sections ?? []).includes(sectionKey)
  const handleConfirmReset = useCallback(async () => {
    try {
      await resetDisplay.mutateAsync({
        uuid: entity.uuid,
        sectionKey,
      })
      toast.success(`${title} reset to default`)
      setResetOpen(false)
    } catch (err) {
      toastApiError(err, `Failed to reset ${title.toLowerCase()}`)
    }
  }, [entity.uuid, resetDisplay, sectionKey, title])

  // Configured columns derived from the in-progress edits (not the saved
  // config) so the preview reflects exactly what is on screen.
  const previewColumns = useMemo(
    () => items.map((item) => toDisplayColumnPayload(item, getColumnLabel(item))),
    [getColumnLabel, items],
  )

  // Pick the sample registration that resolves the most configured columns.
  // A preview is only worth showing when a real record can populate more
  // than half of them - otherwise it would just be a wall of hyphens.
  const previewSample = useMemo(() => {
    if (previewColumns.length === 0 || sampleHoldings.length === 0) {
      return { holding: null as Holding | null, resolved: 0 }
    }
    let bestHolding: Holding | null = null
    let bestResolved = -1
    for (const holding of sampleHoldings) {
      const resolved = buildRegistrationConfiguredFieldItems({
        holding,
        columns: previewColumns,
        entityTypes: allEntities,
        statusWorkflowKey: STATUS_WORKFLOW_KEY,
        locale,
        currency,
      }).filter((item) => isResolvedDisplayValue(item.value)).length
      if (resolved > bestResolved) {
        bestResolved = resolved
        bestHolding = holding
      }
    }
    return { holding: bestHolding, resolved: Math.max(bestResolved, 0) }
  }, [allEntities, currency, locale, previewColumns, sampleHoldings])

  const canPreview =
    previewColumns.length > 0 &&
    previewSample.holding != null &&
    previewSample.resolved * 2 > previewColumns.length

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            {title}
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            {description}
          </p>
        </div>
        <div className="w-[280px] shrink-0">
          <SearchableSelect
            key={items.map((item) => item.id).join('|')}
            value=""
            onValueChange={(id) => {
              const selected = availableItemById.get(id)
              if (!selected) return
              setItems((current) => [...current, selected])
              setLabels((current) => ({
                ...current,
                [selected.id]: selected.attribute.display_name,
              }))
            }}
            groups={availableColumnGroups}
            placeholder={availableItems.length === 0 ? allSelectedPlaceholder : `+ ${addPlaceholder}`}
            searchPlaceholder={searchPlaceholder}
            emptyMessage="No matching attributes"
            disabled={availableItems.length === 0}
            triggerClassName="h-8 text-[12px]"
            contentClassName="w-[360px]"
            renderOption={(option) => {
              const selected = availableItemById.get(option.value)
              if (!selected) return
              return (
                <div className="min-w-0">
                  <div className="truncate text-[13px]">{selected.attribute.display_name}</div>
                  <div className="mt-0.5 whitespace-normal break-words font-mono text-[10px] text-[var(--ink-3)]">
                    {[...(selected.relationshipPath ?? []), selected.attribute.code_name].join('.')}
                  </div>
                </div>
              )
            }}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
        {items.length === 0 ? (
          <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
            {emptyMessage}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
              <div className="w-3.5 shrink-0" />
              <span className="flex-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Attribute
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Source
              </span>
            </div>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item, index) => (
                  <SortableDisplayColumnRow
                    key={item.id}
                    item={item}
                    label={getColumnLabel(item)}
                    onEdit={() => setEditingLabelItem(item)}
                    onRemove={() =>
                      setItems((current) => {
                        setLabels((labelsCurrent) => {
                          const next = { ...labelsCurrent }
                          delete next[item.id]
                          return next
                        })
                        return current.filter((candidate) => candidate.id !== item.id)
                      })
                    }
                    isFirst={index === 0}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2">
        {canReset ? (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setResetOpen(true)}
            disabled={resetDisplay.isPending}
          >
            Reset to default
          </Button>
        ) : null}
        {canPreview ? (
          <Button
            variant="outline"
            size="xs"
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="xs"
          onClick={handleSave}
          disabled={updateEntity.isPending || allDisplayItems.length === 0}
        >
          {updateEntity.isPending ? 'Saving...' : `Save ${title.toLowerCase()}`}
        </Button>
      </div>
      {canPreview ? (
        <DisplayPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          entity={entity}
          sectionKey={sectionKey}
          title={title}
          columns={previewColumns}
          holdings={sampleHoldings}
          entityTypes={allEntities}
          previewHolding={previewSample.holding}
          locale={locale}
          currency={currency}
        />
      ) : null}
      <ConfirmationDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={`Reset ${title.toLowerCase()} to default?`}
        description={`This replaces the current ${title.toLowerCase()} layout with the platform default and saves it immediately. Your current configuration for this section will be overwritten.`}
        confirmLabel="Reset to default"
        pendingLabel="Resetting..."
        isPending={resetDisplay.isPending}
        onConfirm={handleConfirmReset}
      />
      <DisplayColumnEditDialog
        item={editingLabelItem}
        label={editingLabelItem ? getColumnLabel(editingLabelItem) : ''}
        allDisplayItems={allDisplayItems}
        allEntities={allEntities}
        title={labelDialogTitle}
        description={labelDialogDescription}
        fieldLabel={labelFieldLabel}
        open={!!editingLabelItem}
        onOpenChange={(next) => {
          if (!next) setEditingLabelItem(null)
        }}
        onSave={(result) => {
          const original = editingLabelItem
          if (!original) return

          // Remap: user picked a different source attribute. Swap the
          // item in-place so order is preserved, and rebuild the labels
          // map so we don't leak the old key after save.
          if (result.itemId !== original.id) {
            const replacement = allDisplayItems.find(
              (candidate) => candidate.id === result.itemId,
            )
            if (replacement) {
              setItems((current) =>
                current.map((candidate) =>
                  candidate.id === original.id ? replacement : candidate,
                ),
              )
              setLabels((current) => {
                const next = { ...current }
                delete next[original.id]
                next[result.itemId] = result.label
                return next
              })
            }
          } else {
            setLabels((current) => ({
              ...current,
              [result.itemId]: result.label,
            }))
          }

          // Computed-config edits live on the EntityAttributeDefinition
          // itself, so they need a separate write against the owning
          // entity. Look up the entity by key (relationship-sourced
          // columns can target a different entity than the one we're
          // currently editing).
          if (result.computed) {
            const owner = allEntities.find(
              (candidate) => candidate.key === result.computed!.entityKey,
            )
            if (!owner) {
              toast.error(
                `Cannot save computed config: entity "${result.computed.entityKey}" not found`,
              )
            } else {
              updateAttributeAcrossEntities
                .mutateAsync({
                  entityUuid: owner.uuid,
                  attrUuid: result.computed.attributeUuid,
                  configuration_data: result.computed.configuration_data,
                })
                .then(() => {
                  toast.success('Computed value updated')
                })
                .catch((err) => {
                  toastApiError(err, 'Failed to update computed value')
                })
            }
          }
        }}
      />
    </section>
  )
}

export function EntityRegistrationTab({
  entity,
  allEntities,
  attributes,
  onEdit,
  onDragEnd,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  attributes: EntityAttributeDefinition[]
  onEdit: (attribute: EntityAttributeDefinition) => void
  onDragEnd: (event: DragEndEvent) => void
}) {
  const updateEntity = useUpdateEntityType()
  const preview = useEntityRegistrationPreview(entity.uuid)
  const [draft, setDraft] = useState<EntityRegistrationConfig>(
    () => normaliseRegistrationConfig(entity.metadata?.registration),
  )
  const [previewOpen, setPreviewOpen] = useState(false)
  const rulesEnabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  const { data: validationRules = [], isLoading: validationRulesLoading } =
    useValidationRules({
      enabled: rulesEnabled,
      scope: 'entity_registration',
      entityType: entity.uuid,
    })
  const deleteValidationRule = useDeleteValidationRule()
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ValidationRule | null>(null)
  const [deletingRule, setDeletingRule] = useState<ValidationRule | null>(null)

  const { data: availabilityRules = [], isLoading: availabilityRulesLoading } =
    useAvailabilityRules(
      rulesEnabled ? { scope: 'entity_registration', scope_id: entity.uuid } : undefined,
    )
  const deleteAvailabilityRule = useDeleteAvailabilityRule()
  const [availRuleDialogOpen, setAvailRuleDialogOpen] = useState(false)
  const [editingAvailRule, setEditingAvailRule] = useState<AvailabilityRule | null>(null)
  const [deletingAvailRule, setDeletingAvailRule] = useState<AvailabilityRule | null>(null)

  const segments = useMemo(
    () =>
      [...(draft.segments ?? [])].sort(
        (a, b) => a.order - b.order || a.label.localeCompare(b.label),
      ),
    [draft.segments],
  )

  async function saveRegistrationConfig(nextDraft = draft) {
    try {
      await updateEntity.mutateAsync({
        uuid: entity.uuid,
        metadata: {
          ...(entity.metadata ?? {}),
          registration: nextDraft,
        },
      })
      toast.success('Entity registration settings saved')
    } catch (err) {
      toastApiError(err, 'Failed to save entity registration settings')
    }
  }

  function updateDraft(updater: (current: EntityRegistrationConfig) => EntityRegistrationConfig) {
    setDraft((current) => updater(current))
  }

  function addSegmentFromRelationships(relationshipKeys: string[]) {
    const relationships = relationshipKeys
      .map((key) => entity.relationships.find((relationship) => relationship.key === key))
      .filter((relationship): relationship is EntityRelationshipDefinition => Boolean(relationship))
    if (relationships.length === 0) return
    const key = relationships.map((relationship) => relationship.key).join('_')
    if (segments.some((segment) => segment.key === key)) return
    const segment: EntityRegistrationSegment = {
      key,
      label: relationships.map((relationship) => relationship.target_label).join(' & '),
      order: segments.length,
      members: relationships.map((relationship) => ({
        relationship_key: relationship.key,
        target_entity_key: relationship.target_key,
      })),
    }
    updateDraft((current) => ({
      ...current,
      segments: [...(current.segments ?? []), segment],
    }))
  }

  function removeSegment(key: string) {
    updateDraft((current) => ({
      ...current,
      segments: (current.segments ?? [])
        .filter((segment) => segment.key !== key)
        .map((segment, index) => ({ ...segment, order: index })),
    }))
  }

  function moveSegment(key: string, direction: -1 | 1) {
    const currentIndex = segments.findIndex((segment) => segment.key === key)
    const nextIndex = currentIndex + direction
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= segments.length) return
    const next = [...segments]
    ;[next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]]
    updateDraft((current) => ({
      ...current,
      segments: next.map((segment, index) => ({ ...segment, order: index })),
    }))
  }

  function handleSegmentDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = segments.findIndex((segment) => segment.key === active.id)
    const newIndex = segments.findIndex((segment) => segment.key === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const ordered = arrayMove(segments, oldIndex, newIndex).map((segment, index) => ({
      ...segment,
      order: index,
    }))
    updateDraft((current) => ({ ...current, segments: ordered }))
  }

  const relationshipPairs = useMemo(() => {
    const pairs: EntityRelationshipDefinition[][] = []
    for (let i = 0; i < entity.relationships.length; i += 1) {
      for (let j = i + 1; j < entity.relationships.length; j += 1) {
        pairs.push([entity.relationships[i], entity.relationships[j]])
      }
    }
    return pairs
  }, [entity.relationships])

  async function handleDeleteValidationRule() {
    if (!deletingRule) return
    try {
      await deleteValidationRule.mutateAsync(deletingRule.uuid)
      toast.success('Validation rule deleted')
      setDeletingRule(null)
      if (editingRule?.uuid === deletingRule.uuid) {
        setEditingRule(null)
        setRuleDialogOpen(false)
      }
    } catch (err) {
      toastApiError(err, 'Failed to delete validation rule')
    }
  }

  async function handleDeleteAvailabilityRule() {
    if (!deletingAvailRule) return
    try {
      await deleteAvailabilityRule.mutateAsync(deletingAvailRule.uuid)
      toast.success('Availability rule deleted')
      setDeletingAvailRule(null)
      if (editingAvailRule?.uuid === deletingAvailRule.uuid) {
        setEditingAvailRule(null)
        setAvailRuleDialogOpen(false)
      }
    } catch (err) {
      toastApiError(err, 'Failed to delete availability rule')
    }
  }

  return (
    <section className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Registration
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Configure fields, form flow, and rules for this entity&apos;s registration surface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" type="button" onClick={() => setPreviewOpen(true)}>
            <Eye size={13} />
            Preview
          </Button>
          <Button
            size="sm"
            type="button"
            onClick={() => void saveRegistrationConfig()}
            disabled={updateEntity.isPending}
          >
            {updateEntity.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Registration attributes */}
      <section className="flex flex-col gap-3">
        <div>
          <h4 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Registration attributes
          </h4>
          <p className="text-[10px] text-[var(--ink-3)]">
            Drag to change generated form order. Configure required state and visibility per field.
          </p>
        </div>
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {attributes.length === 0 ? (
            <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
              No registration-backed attributes yet.
            </div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={attributes.map((attribute) => attribute.uuid)}
                strategy={verticalListSortingStrategy}
              >
                {attributes.map((attribute, index) => (
                  <SortableEntityRegistrationAttributeRow
                    key={attribute.uuid}
                    attribute={attribute}
                    isFirst={index === 0}
                    onEdit={() => onEdit(attribute)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </section>

      {/* Registration settings - segments */}
      <section className="flex flex-col gap-3">
        <div>
          <h4 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Registration settings
          </h4>
          <p className="text-[10px] text-[var(--ink-3)]">
            Control the multi-step preview flow and segment order.
          </p>
        </div>

        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            updateDraft((current) => ({
              ...current,
              segments_enabled: !current.segments_enabled,
            }))
          }
          className="h-auto w-full justify-start gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 text-left hover:border-[var(--green-border)]"
        >
          <Switch checked={Boolean(draft.segments_enabled)} decorative />
          <div>
            <span className="block text-[13px] text-[var(--ink)]">Enable segments</span>
            <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
              Switch the preview from a single generated form to a multi-step flow.
            </span>
          </div>
        </Button>

        {draft.segments_enabled ? (
          <>
            <div>
              <h5 className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
                Segment order
              </h5>
              <p className="text-[10px] text-[var(--ink-3)]">
                Segments can contain one relationship entity or a pair of relationship entities.
              </p>
            </div>
            <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
              {segments.length === 0 ? (
                <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                  No segments configured yet.
                </div>
              ) : (
                <DndContext collisionDetection={closestCenter} onDragEnd={handleSegmentDragEnd}>
                  <SortableContext
                    items={segments.map((segment) => segment.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {segments.map((segment, index) => (
                      <SortableRegistrationSegmentRow
                        key={segment.key}
                        segment={segment}
                        index={index}
                        isFirst={index === 0}
                        isLast={index === segments.length - 1}
                        onMove={(direction) => moveSegment(segment.key, direction)}
                        onRemove={() => removeSegment(segment.key)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
            <div>
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
                Add segment
              </p>
              <div className="flex flex-wrap gap-2">
                {entity.relationships.map((relationship) => (
                  <Button
                    key={relationship.key}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addSegmentFromRelationships([relationship.key])}
                  >
                    + {relationship.target_label}
                  </Button>
                ))}
                {relationshipPairs.map(([first, second]) => (
                  <Button
                    key={`${first.key}-${second.key}`}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addSegmentFromRelationships([first.key, second.key])}
                  >
                    + {first.target_label} & {second.target_label}
                  </Button>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </section>

      {/* Validation rules */}
      {rulesEnabled ? (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                Validation rules
              </h4>
              <p className="text-[10px] text-[var(--ink-3)]">
                Run before a {entity.label.toLowerCase()} record is created. Block or warn on bad
                data.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingRule(null)
                setRuleDialogOpen(true)
              }}
            >
              <Plus size={13} />
              Add rule
            </Button>
          </div>
          <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
            {validationRulesLoading ? (
              <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                Loading validation rules...
              </div>
            ) : validationRules.length === 0 ? (
              <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                No validation rules yet.
              </div>
            ) : (
              validationRules.map((rule, index) => (
                <div
                  key={rule.uuid}
                  className={cn(
                    'flex items-center gap-3 bg-[var(--bg)] px-3 py-2',
                    index > 0 && 'border-t-[0.5px] border-t-[var(--rule)]',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[12px] text-[var(--ink)]">
                        {rule.name}
                      </span>
                      <Badge
                        variant={
                          rule.severity === 'BLOCK'
                            ? 'destructive'
                            : rule.severity === 'WARN'
                              ? 'secondary'
                              : 'outline'
                        }
                        className="text-[10px] uppercase tracking-[0.08em]"
                      >
                        {rule.severity.toLowerCase()}
                      </Badge>
                      <Badge variant={rule.is_active ? 'outline' : 'ghost'} className="text-[10px]">
                        {rule.is_active ? 'active' : 'inactive'}
                      </Badge>
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-[var(--ink-3)]">
                      {rule.message || 'No message configured'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => {
                      setEditingRule(rule)
                      setRuleDialogOpen(true)
                    }}
                    aria-label={`Edit ${rule.name}`}
                  >
                    <Pencil size={12} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setDeletingRule(rule)}
                    aria-label={`Delete ${rule.name}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}

      {/* Availability rules */}
      {rulesEnabled ? (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                Availability rules
              </h4>
              <p className="text-[10px] text-[var(--ink-3)]">
                Gate whether this entity&apos;s registration surface is available based on
                registration context.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingAvailRule(null)
                setAvailRuleDialogOpen(true)
              }}
            >
              <Plus size={13} />
              Add rule
            </Button>
          </div>
          <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
            {availabilityRulesLoading ? (
              <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                Loading availability rules...
              </div>
            ) : availabilityRules.length === 0 ? (
              <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                No availability rules yet.
              </div>
            ) : (
              availabilityRules.map((rule, index) => (
                <div
                  key={rule.uuid}
                  className={cn(
                    'flex items-center gap-3 bg-[var(--bg)] px-3 py-2',
                    index > 0 && 'border-t-[0.5px] border-t-[var(--rule)]',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[12px] text-[var(--ink)]">
                        {rule.name}
                      </span>
                      <Badge
                        variant={
                          rule.severity === 'BLOCK'
                            ? 'destructive'
                            : rule.severity === 'WARN'
                              ? 'secondary'
                              : 'outline'
                        }
                        className="text-[10px] uppercase tracking-[0.08em]"
                      >
                        {rule.severity.toLowerCase()}
                      </Badge>
                      <Badge variant={rule.is_active ? 'outline' : 'ghost'} className="text-[10px]">
                        {rule.is_active ? 'active' : 'inactive'}
                      </Badge>
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-[var(--ink-3)]">
                      {rule.message || 'No message configured'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => {
                      setEditingAvailRule(rule)
                      setAvailRuleDialogOpen(true)
                    }}
                    aria-label={`Edit ${rule.name}`}
                  >
                    <Pencil size={12} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setDeletingAvailRule(rule)}
                    aria-label={`Delete ${rule.name}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}

      <ValidationRuleDialog
        open={ruleDialogOpen}
        onOpenChange={(next) => {
          setRuleDialogOpen(next)
          if (!next) setEditingRule(null)
        }}
        rule={editingRule}
        entity={entity}
        defaultScope="entity_registration"
      />

      <EntityAvailabilityRuleDialog
        open={availRuleDialogOpen}
        onOpenChange={(next) => {
          setAvailRuleDialogOpen(next)
          if (!next) setEditingAvailRule(null)
        }}
        editing={editingAvailRule}
        entity={entity}
      />

      <ConfirmationDialog
        open={!!deletingRule}
        onOpenChange={(next) => {
          if (!next) setDeletingRule(null)
        }}
        title={`Delete ${deletingRule?.name ?? 'validation rule'}?`}
        description="This validation rule will stop running for future registrations."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteValidationRule.isPending}
        onConfirm={handleDeleteValidationRule}
      />

      <ConfirmationDialog
        open={!!deletingAvailRule}
        onOpenChange={(next) => {
          if (!next) setDeletingAvailRule(null)
        }}
        title={`Delete ${deletingAvailRule?.name ?? 'availability rule'}?`}
        description="This availability rule will no longer gate this entity's registration."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteAvailabilityRule.isPending}
        onConfirm={handleDeleteAvailabilityRule}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[840px] sm:max-w-[840px]">
          <DialogHeader>
            <DialogTitle>Preview {entity.label} registration</DialogTitle>
            <DialogDescription>
              Runs the generated form and rules simulation without writing records.
            </DialogDescription>
          </DialogHeader>
          <EntityRegistrationFormRenderer
            entity={entity}
            allEntities={allEntities}
            registration={draft}
            previewResult={preview.data ?? null}
            onEvaluate={async ({ step, values }) => {
              await preview.mutateAsync({ step, registration: draft, values })
            }}
          />
        </DialogContent>
      </Dialog>
    </section>
  )
}

/**
 * EntityRegistrationSection - self-contained registration tab for any entity.
 * Wraps EntityRegistrationTab + EntityRegistrationAttributeDialog and owns
 * all the plumbing (filtered attributes, drag-end reorder, edit-dialog state).
 *
 * Use this from any page (Entity Editor, Product Templates, Host Templates,
 * Transactions, etc.) so every surface drives the same shared implementation.
 */
export function EntityRegistrationSection({
  entity,
  allEntities,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
}) {
  const reorder = useReorderEntityAttributes(entity.uuid)
  const [editingRegistrationAttribute, setEditingRegistrationAttribute] =
    useState<EntityAttributeDefinition | null>(null)

  const sortedAttributes = useMemo(
    () =>
      [...(entity.attributes ?? [])].sort(
        (a, b) => a.order - b.order || a.display_name.localeCompare(b.display_name),
      ),
    [entity.attributes],
  )
  const registrationAttributes = useMemo(
    () => sortedAttributes.filter(isRegistrationAttribute),
    [sortedAttributes],
  )

  const handleRegistrationDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = registrationAttributes.findIndex((attr) => attr.uuid === active.id)
      const newIndex = registrationAttributes.findIndex((attr) => attr.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const ordered = arrayMove(registrationAttributes, oldIndex, newIndex).map(
        (attr, index) => ({ uuid: attr.uuid, order: index }),
      )
      reorder.mutate(ordered, {
        onError: (err) =>
          toastApiError(err, 'Failed to reorder registration attributes'),
      })
    },
    [registrationAttributes, reorder],
  )

  return (
    <>
      <EntityRegistrationTab
        entity={entity}
        allEntities={allEntities}
        attributes={registrationAttributes}
        onEdit={setEditingRegistrationAttribute}
        onDragEnd={handleRegistrationDragEnd}
      />
      <EntityRegistrationAttributeDialog
        entity={entity}
        attribute={editingRegistrationAttribute ?? undefined}
        open={!!editingRegistrationAttribute}
        onOpenChange={(next) => {
          if (!next) setEditingRegistrationAttribute(null)
        }}
      />
    </>
  )
}

function SortableEntityRegistrationAttributeRow({
  attribute,
  isFirst,
  onEdit,
}: {
  attribute: EntityAttributeDefinition
  isFirst: boolean
  onEdit: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: attribute.uuid })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }
  return (
    <div ref={setNodeRef} style={style} className={cn(!isFirst && 'border-t-[0.5px] border-t-[var(--rule)]')}>
      <EntityRegistrationAttributeRow
        attribute={attribute}
        onEdit={onEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

function EntityRegistrationAttributeRow({
  attribute,
  onEdit,
  dragHandleProps,
}: {
  attribute: EntityAttributeDefinition
  onEdit: () => void
  dragHandleProps?: Record<string, unknown>
}) {
  const config = attribute.configuration_data ?? {}
  const registration = getRegistrationConfig(attribute)
  const hasVisibilityConditions = Boolean(registration.visibility_conditions)

  return (
    <div className="flex items-center gap-2 bg-[var(--bg)] px-3 py-2">
      {dragHandleProps ? (
        <div
          className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
          {...dragHandleProps}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[var(--ink)]">
            {attribute.display_name}
          </span>
          <Badge variant="outline" className="font-mono text-[10px]">
            {attribute.code_name}
          </Badge>
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.08em]">
            {attribute.field_type.toLowerCase().replace(/_/g, ' ')}
          </Badge>
          {attribute.required ? (
            <Badge variant="destructive" className="text-[10px] uppercase tracking-[0.08em]">
              required
            </Badge>
          ) : (
            <Badge variant="ghost" className="text-[10px] uppercase tracking-[0.08em]">
              optional
            </Badge>
          )}
          {config.lookup_provider ? (
            <Badge variant="outline" className="text-[10px]">
              lookup: {lookupMechanismLabel(config.lookup_provider)}
            </Badge>
          ) : null}
          {hasVisibilityConditions ? (
            <Badge variant="secondary" className="text-[10px]">
              visibility rules
            </Badge>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
          {attribute.description || 'Captured during registration.'}
        </p>
      </div>
      <Button variant="ghost" size="icon-xs" onClick={onEdit} title="Edit registration settings">
        <Pencil size={13} />
      </Button>
    </div>
  )
}

function SortableRegistrationSegmentRow({
  segment,
  index,
  isFirst,
  isLast,
  onMove,
  onRemove,
}: {
  segment: EntityRegistrationSegment
  index: number
  isFirst: boolean
  isLast: boolean
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: segment.key })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 bg-[var(--bg)] px-3 py-2',
        !isFirst && 'border-t-[0.5px] border-t-[var(--rule)]',
      )}
    >
      <div
        className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[var(--ink)]">{segment.label}</span>
          <Badge variant="outline" className="font-mono text-[10px]">
            {segment.key}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {segment.members.length} {segment.members.length === 1 ? 'entity' : 'entities'}
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
          {segment.members.map((member) => member.target_entity_key ?? member.relationship_key).join(' + ')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button type="button" variant="ghost" size="icon-xs" disabled={index === 0} onClick={() => onMove(-1)}>
          <ArrowUpToLine size={13} />
        </Button>
        <Button type="button" variant="ghost" size="icon-xs" disabled={isLast} onClick={() => onMove(1)}>
          <ArrowDownToLine size={13} />
        </Button>
        <Button type="button" variant="ghost" size="icon-xs" onClick={onRemove}>
          <X size={13} />
        </Button>
      </div>
    </div>
  )
}

function normaliseRegistrationConfig(
  config: EntityRegistrationConfig | undefined,
): EntityRegistrationConfig {
  return {
    segments_enabled: Boolean(config?.segments_enabled),
    segments: (config?.segments ?? []).map((segment, index) => ({
      key: segment.key,
      label: segment.label,
      order: segment.order ?? index,
      members: segment.members ?? [],
    })),
    custom_fields: (config?.custom_fields ?? []).map((field, index) => ({
      ...field,
      order: field.order ?? index,
      required: Boolean(field.required),
      configuration_data: field.configuration_data ?? {},
      validation_rules: field.validation_rules ?? [],
      visibility_conditions: field.visibility_conditions ?? null,
    })),
  }
}

// ---------------------------------------------------------------------------
// EntityAvailabilityRuleDialog - create/edit an entity_registration-scoped
// AvailabilityRule. Reuses the same condition-builder pattern as the
// warranty-class RuleDialog but styled for the entity editor.
// ---------------------------------------------------------------------------

type AvailabilityRuleFormState = {
  name: string
  description: string
  conditions: PredicateNode | null
  severity: Severity
  message: string
  effective_from: string
  effective_until: string
  is_active: boolean
}

const AVAIL_RULE_FORM_EMPTY: AvailabilityRuleFormState = {
  name: '',
  description: '',
  conditions: null,
  severity: 'BLOCK',
  message: '',
  effective_from: '',
  effective_until: '',
  is_active: true,
}

const AVAIL_SEVERITIES: { value: Severity; label: string; hint: string }[] = [
  { value: 'BLOCK', label: 'Block', hint: 'Prevent this registration from proceeding' },
  { value: 'WARN', label: 'Warn', hint: 'Show a warning but allow it to proceed' },
  { value: 'INFO', label: 'Info', hint: 'Informational only' },
]

function EntityAvailabilityRuleDialog({
  open,
  onOpenChange,
  editing,
  entity,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: AvailabilityRule | null
  entity: EntityTypeDefinition
}) {
  const createRule = useCreateAvailabilityRule()
  const updateRule = useUpdateAvailabilityRule()
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs({
    enabled: open,
    entityKey: entity.key,
  })
  const isEdit = !!editing

  const [form, setForm] = useState<AvailabilityRuleFormState>(AVAIL_RULE_FORM_EMPTY)
  const [seedKey, setSeedKey] = useState('')
  const nextKey = open ? (editing?.uuid ?? 'new') : '__closed__'
  if (nextKey !== seedKey) {
    setSeedKey(nextKey)
    if (!open) {
      setForm(AVAIL_RULE_FORM_EMPTY)
    } else if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        conditions: editing.conditions,
        severity: editing.severity,
        message: editing.message ?? '',
        effective_from: editing.effective_from ?? '',
        effective_until: editing.effective_until ?? '',
        is_active: editing.is_active,
      })
    } else {
      setForm(AVAIL_RULE_FORM_EMPTY)
    }
  }

  function setField<K extends keyof AvailabilityRuleFormState>(
    key: K,
    value: AvailabilityRuleFormState[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Rule name is required')
      return
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      conditions: form.conditions,
      severity: form.severity,
      message: form.message.trim() || null,
      scope: 'entity_registration' as const,
      scope_id: entity.uuid,
      is_active: form.is_active,
      effective_from: form.effective_from || null,
      effective_until: form.effective_until || null,
    }
    try {
      if (isEdit && editing) {
        await updateRule.mutateAsync({ uuid: editing.uuid, ...payload })
        toast.success('Availability rule updated')
      } else {
        await createRule.mutateAsync(payload)
        toast.success('Availability rule created')
      }
      setForm(AVAIL_RULE_FORM_EMPTY)
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, isEdit ? 'Failed to update rule' : 'Failed to create rule')
    }
  }

  const isPending = createRule.isPending || updateRule.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit availability rule' : 'Add availability rule'}</DialogTitle>
          <DialogDescription>
            Gates whether this {entity.label.toLowerCase()} registration surface is available. All
            conditions must match for the rule to fire.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Rule name
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Commercial properties only"
                className="h-9 text-[13px]"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Description
              </Label>
              <Input
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Optional admin note"
                className="h-9 text-[13px]"
              />
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Conditions
            </Label>
            {refsLoading ? (
              <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                Loading fields…
              </div>
            ) : (
              <ConditionBuilder
                value={form.conditions}
                onChange={(next) => setField('conditions', next)}
                availableRefs={refs}
                byRef={byRef}
              />
            )}
            <p className="mt-1.5 text-[10px] text-[var(--ink-3)]">
              Leave empty to always match (unconditional availability gate).
            </p>
          </div>

          <div>
            <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Severity
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {AVAIL_SEVERITIES.map((s) => {
                const active = form.severity === s.value
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setField('severity', s.value)}
                    className={cn(
                      'flex flex-col items-start gap-0.5 rounded-[6px] border-[0.5px] px-3 py-2 text-left transition-all duration-150',
                      active
                        ? 'border-[var(--green)] bg-[var(--green)]/5 text-[var(--ink)]'
                        : 'border-[var(--rule)] hover:border-[var(--rule-strong)]',
                    )}
                  >
                    <span className="text-[12px] font-medium leading-tight">{s.label}</span>
                    <span className="text-[10px] leading-tight text-[var(--ink-3)]">{s.hint}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Message
            </Label>
            <Input
              value={form.message}
              onChange={(e) => setField('message', e.target.value)}
              placeholder="Shown when the rule fires (optional)"
              className="h-9 text-[13px]"
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] px-3 py-2.5">
            <div>
              <p className="text-[12px] text-[var(--ink)]">Rule active</p>
              <p className="text-[11px] text-[var(--ink-3)]">
                When off, this rule is ignored without deleting it.
              </p>
            </div>
            <Switch
              checked={form.is_active}
              aria-label="Toggle rule active"
              onCheckedChange={(checked) => setField('is_active', checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={isPending}>
            {isPending ? 'Saving...' : isEdit ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SortableEntityAttributeRow(props: {
  attribute: EntityAttributeDefinition
  isFirst: boolean
  onEdit: () => void
  onDelete: () => void
  hideDelete?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: props.attribute.uuid,
    })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(!props.isFirst && 'border-t-[0.5px] border-t-[var(--rule)]')}
    >
      <EntityAttributeRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

export function EntityAttributeRow({
  attribute,
  onEdit,
  onDelete,
  dragHandleProps,
  hideDelete,
}: {
  attribute: EntityAttributeDefinition
  isFirst?: boolean
  onEdit: () => void
  onDelete: () => void
  dragHandleProps?: Record<string, unknown>
  hideDelete?: boolean
}) {
  const config = attribute.configuration_data ?? {}
  const valueSource = config.value_source ?? 'manual'
  const optionCount = Array.isArray(config.options) ? config.options.length : 0
  const isGeneratedComputed =
    attribute.source === 'COMPUTED' &&
    (valueSource === 'workflow_state' || valueSource === 'entity_relationship')
  const canDelete = attribute.source === 'COMPUTED'
    ? !isGeneratedComputed
    : attribute.editable
  const isLocked = !attribute.editable

  return (
    <div
      className={cn(
        'flex items-center gap-2 bg-[var(--bg)] px-3 py-2',
        isLocked && 'bg-[var(--bg-subtle)] opacity-[0.65]',
      )}
    >
      {dragHandleProps ? (
        <div
          className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
          {...dragHandleProps}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[var(--ink)]">
            {attribute.display_name}
          </span>
          {isLocked ? (
            <Badge variant="outline" className="gap-1 text-[10px]">
              <Lock className="h-2.5 w-2.5" />
              locked
            </Badge>
          ) : null}
          <Badge variant="outline" className="font-mono text-[10px]">
            {attribute.code_name}
          </Badge>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.08em]">
            {attribute.source.toLowerCase()}
          </Badge>
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.08em]">
            {attribute.field_type.toLowerCase().replace(/_/g, ' ')}
          </Badge>
          {valueSource === 'workflow_state' ? (
            <Badge variant="outline" className="text-[10px]">
              workflow: {config.workflow_key || attribute.code_name}
            </Badge>
          ) : null}
          {valueSource === 'registration_field' ? (
            <Badge variant="outline" className="text-[10px]">
              registration field
            </Badge>
          ) : null}
          {config.lookup_provider ? (
            <Badge variant="outline" className="text-[10px]">
              lookup: {lookupMechanismLabel(config.lookup_provider)}
            </Badge>
          ) : null}
          {optionCount > 0 ? (
            <Badge variant="outline" className="text-[10px]">
              {optionCount} options
            </Badge>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
          {attribute.description ||
            (attribute.column_name ? `Column: ${attribute.column_name}` : 'Entity attribute')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onEdit}
          title={isLocked ? 'Core attributes cannot be edited' : 'Edit attribute'}
          disabled={isLocked}
        >
          <Pencil size={13} />
        </Button>
        {!hideDelete ? (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onDelete}
            title={
              canDelete
                ? 'Delete attribute'
                : isLocked
                  ? 'Core attributes cannot be deleted'
                : 'Generated attributes cannot be deleted directly'
            }
            disabled={!canDelete}
          >
            <Trash2 size={13} />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function EntityRelationshipRow({
  relationship,
  onEdit,
  onDelete,
}: {
  relationship: EntityRelationshipDefinition
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[12px] text-[var(--ink)]">{relationship.label}</p>
          <Badge variant="secondary" className="text-[10px]">
            {relationship.cardinality === 'MANY' ? 'one-to-many' : 'one-to-one'}
          </Badge>
        </div>
        <p className="text-[10px] text-[var(--ink-3)]">
          Maps to {relationship.target_label}
        </p>
      </div>
      <Badge variant="secondary" className="text-[10px]">
        {relationship.target_label}
      </Badge>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon-xs" onClick={onEdit} title="Edit relationship">
          <Pencil size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onDelete}
          title="Delete relationship"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  )
}

function EntityRelationshipDialog({
  entity,
  allEntities,
  relationship,
  open,
  onOpenChange,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  relationship?: EntityRelationshipDefinition
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const create = useCreateEntityRelationship(entity.uuid)
  const update = useUpdateEntityRelationship(entity.uuid)

  const targetOptions = useMemo(
    () =>
      allEntities
        .filter(canSelectRelationshipTarget)
        .map((target) => ({
          value: target.key,
          label: `${target.label} (${target.key})`,
        })),
    [allEntities],
  )

  const initial = useMemo<EntityRelationshipFormValues>(
    () => ({
      label: relationship?.label ?? '',
      target_entity_key: relationship?.target_key ?? '',
      cardinality: relationship?.cardinality ?? 'ONE',
    }),
    [relationship],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        value: initial.label,
        required: 'Label is required',
        className: 'col-span-2',
      },
      {
        type: 'select',
        name: 'target_entity_key',
        label: 'Target entity',
        placeholder: 'Select entity',
        value: initial.target_entity_key,
        options: targetOptions,
        required: 'Target entity is required',
        className: 'col-span-1',
      },
      {
        type: 'select',
        name: 'cardinality',
        label: 'Cardinality',
        value: initial.cardinality,
        options: CARDINALITY_OPTIONS,
        className: 'col-span-1',
      },
    ],
    [initial, targetOptions],
  )

  const { Form, ref } = useForm(
    {
      name: `entity-relationship-${entity.key}-${relationship?.uuid ?? 'new'}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as EntityRelationshipFormValues
      const payload = {
        label: values.label.trim(),
        key: relationship?.key ?? toCodeName(values.label),
        target_entity_key: values.target_entity_key,
        cardinality: values.cardinality,
        via: relationship?.via ?? toCodeName(values.label),
      }

      try {
        if (relationship) {
          await update.mutateAsync({ relUuid: relationship.uuid, ...payload })
          toast.success('Entity relationship updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Entity relationship created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to save entity relationship')
      }
    },
    [open, fields, relationship?.uuid],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) ref.current?.resetForm()
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>
            {relationship ? 'Edit Relationship' : 'New Relationship'}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => ref.current?.submitForm()} disabled={busy}>
            {busy ? 'Saving…' : relationship ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function EntityRegistrationAttributeDialog({
  entity,
  attribute,
  open,
  onOpenChange,
}: {
  entity: EntityTypeDefinition
  attribute?: EntityAttributeDefinition
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const update = useUpdateEntityAttribute(entity.uuid)
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs({
    enabled: open,
  })

  const initial = useMemo<EntityRegistrationAttributeFormValues>(
    () => {
      const lookupProvider = String(attribute?.configuration_data?.lookup_provider ?? '')
      const supportedLookup = LOOKUP_MECHANISM_OPTIONS.some(
        (option) => option.value === lookupProvider,
      )
      return {
        required: attribute?.required ?? false,
        lookup_enabled: Boolean(lookupProvider),
        lookup_provider: supportedLookup ? lookupProvider : 'address',
        visibility_conditions:
          getRegistrationConfig(attribute ?? ({} as EntityAttributeDefinition))
            .visibility_conditions ?? null,
      }
    },
    [attribute],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'checkbox',
        name: 'required',
        label: 'Required during registration',
        value: initial.required,
        className: 'col-span-2',
      },
      {
        type: 'custom',
        name: 'advanced_settings_header',
        value: '',
        className: 'col-span-2 -mb-2',
        render: () => (
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Advanced settings
            </p>
            <p className="mt-1 text-[11px] text-[var(--ink-3)]">
              Optional integration metadata for generated registration surfaces.
            </p>
          </div>
        ),
      },
      {
        type: 'checkbox',
        name: 'lookup_enabled',
        label: 'Enable platform lookup',
        value: initial.lookup_enabled,
        className:
          'col-span-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3',
        hint: 'Use a supported platform lookup control in generated registration surfaces.',
      },
      {
        type: 'select',
        name: 'lookup_provider',
        label: 'Lookup mechanism',
        placeholder: 'Select lookup mechanism',
        value: initial.lookup_provider,
        options: LOOKUP_MECHANISM_OPTIONS,
        visible: (formCtx) => Boolean(formCtx.values.lookup_enabled),
        className:
          'col-span-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3',
        hint: 'Choose one of the lookup mechanisms supported by the platform.',
      },
      {
        type: 'custom',
        name: 'visibility_conditions',
        value: initial.visibility_conditions,
        className: 'col-span-2',
        render: (formCtx) => (
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
            <div className="mb-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Visibility dependencies
              </p>
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Leave empty to always show this attribute during registration.
              </p>
            </div>
            {refsLoading ? (
              <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                Loading fields...
              </div>
            ) : (
              <ConditionBuilder
                value={
                  (formCtx.values.visibility_conditions as PredicateNode | null) ?? null
                }
                onChange={(next) =>
                  formCtx.setFieldValue('visibility_conditions', next)
                }
                availableRefs={refs}
                byRef={byRef}
              />
            )}
          </div>
        ),
      },
      {
        type: 'custom',
        name: 'validation_rules',
        value: '',
        className: 'col-span-2',
        render: () => (
          <RegistrationAttributeValidationRules
            key={attribute?.uuid ?? 'closed'}
            entity={entity}
            attribute={attribute}
            open={open}
          />
        ),
      },
    ],
    [attribute, byRef, entity, initial, open, refs, refsLoading],
  )

  const formConfig = useMemo(
    () => ({
      name: `entity-registration-attribute-${entity.key}-${attribute?.uuid ?? 'closed'}`,
      submit: 'footer' as const,
      fields,
    }),
    [attribute?.uuid, entity.key, fields],
  )

  const { Form, ref } = useForm(
    formConfig,
    async (rawValues) => {
      if (!attribute) return
      const values = rawValues as unknown as EntityRegistrationAttributeFormValues
      const previousConfig = attribute.configuration_data ?? {}
      const previousRegistration =
        previousConfig.registration && typeof previousConfig.registration === 'object'
          ? previousConfig.registration
          : {}
      const configuration_data = {
        ...previousConfig,
        lookup_provider: values.lookup_enabled ? values.lookup_provider.trim() : '',
        registration: {
          ...previousRegistration,
          visibility_conditions: values.visibility_conditions ?? null,
        },
      }
      try {
        await update.mutateAsync({
          attrUuid: attribute.uuid,
          required: Boolean(values.required),
          configuration_data,
        })
        toast.success('Registration attribute updated')
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to save registration settings')
      }
    },
    [open, fields, attribute?.uuid],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) ref.current?.resetForm()
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            Registration Settings
            {attribute ? ` - ${attribute.display_name}` : ''}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RegistrationAttributeValidationRules({
  entity,
  attribute,
  open,
}: {
  entity: EntityTypeDefinition
  attribute?: EntityAttributeDefinition
  open: boolean
}) {
  const rulesEnabled = useFeatureFlag(FLAGS.REGISTRATION_RULES)
  const duplicateRef = getDuplicateRuleRef(entity, attribute)
  const valueRef = getValueRuleRef(entity, attribute)
  const supportsLengthOrPattern = !!(
    valueRef &&
    attribute &&
    LENGTH_PATTERN_FIELD_TYPES.has(attribute.field_type)
  )
  const availableKinds = useMemo<RegistrationRuleKind[]>(() => {
    const kinds: RegistrationRuleKind[] = []
    if (duplicateRef) kinds.push('duplicate')
    if (supportsLengthOrPattern) kinds.push('min_length', 'max_length', 'pattern')
    return kinds
  }, [duplicateRef, supportsLengthOrPattern])

  const hasAnyRefs = !!duplicateRef || !!valueRef
  const { data: rules = [], isLoading } = useValidationRules({
    enabled: open && rulesEnabled && hasAnyRefs,
    scope: 'entity_registration',
    entityType: entity.uuid,
  })
  const create = useCreateValidationRule()
  const update = useUpdateValidationRule()
  const remove = useDeleteValidationRule()

  const matchedRules = useMemo(() => {
    return rules
      .map((rule) => {
        const detected = detectRegistrationRule(rule, { duplicateRef, valueRef })
        return detected ? { rule, ...detected } : null
      })
      .filter(
        (
          entry,
        ): entry is {
          rule: ValidationRule
          kind: RegistrationRuleKind
          length?: number
          pattern?: string
        } => entry !== null,
      )
  }, [rules, duplicateRef, valueRef])

  const initialKind: RegistrationRuleKind = availableKinds[0] ?? 'duplicate'
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRuleUuid, setEditingRuleUuid] = useState<string | null>(null)
  const [advancedRule, setAdvancedRule] = useState<ValidationRule | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [draft, setDraft] = useState<RegistrationRuleDraft>(() =>
    attribute
      ? {
          ...DEFAULT_RULE_DRAFT,
          kind: initialKind,
          message: defaultMessageForKind(initialKind, attribute),
        }
      : DEFAULT_RULE_DRAFT,
  )
  const [deletingRule, setDeletingRule] = useState<ValidationRule | null>(null)

  const editingEntry = matchedRules.find((entry) => entry.rule.uuid === editingRuleUuid) ?? null
  const editingRule = editingEntry?.rule ?? null
  const busy = create.isPending || update.isPending || remove.isPending

  if (!rulesEnabled) return null

  if (!attribute || availableKinds.length === 0) {
    return (
      <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Validation rules
        </p>
        <p className="mt-1 text-[11px] text-[var(--ink-3)]">
          Validation rules aren't supported for this field type yet. Available
          for address, text, email, phone, link, and host-backed columns.
        </p>
      </div>
    )
  }

  function openCreate(kind?: RegistrationRuleKind) {
    if (!attribute) return
    if (!kind) {
      setAdvancedRule(null)
      setAdvancedOpen(true)
      return
    }
    const nextKind = kind ?? availableKinds[0]
    setEditingRuleUuid(null)
    setDraft({
      ...DEFAULT_RULE_DRAFT,
      kind: nextKind,
      length: nextKind === 'min_length' ? 1 : nextKind === 'max_length' ? 50 : 1,
      message: defaultMessageForKind(nextKind, attribute, {
        length: nextKind === 'min_length' ? 1 : 50,
      }),
    })
    setEditorOpen(true)
  }

  function openEdit(entry: {
    rule: ValidationRule
    kind: RegistrationRuleKind
    length?: number
    pattern?: string
  }) {
    if (!attribute) return
    setAdvancedRule(entry.rule)
    setAdvancedOpen(true)
  }

  function closeEditor() {
    setEditorOpen(false)
    setEditingRuleUuid(null)
  }

  function changeKind(kind: RegistrationRuleKind) {
    if (!attribute) return
    setDraft((current) => {
      const wasDefault =
        !current.message ||
        current.message ===
          defaultMessageForKind(current.kind, attribute, {
            length: current.length,
            pattern: current.pattern,
          })
      const nextLength =
        kind === 'max_length' && current.length === 1 ? 50 : current.length
      return {
        ...current,
        kind,
        length: nextLength,
        message: wasDefault
          ? defaultMessageForKind(kind, attribute, {
              length: nextLength,
              pattern: current.pattern,
            })
          : current.message,
      }
    })
  }

  async function saveRule() {
    if (!attribute) return
    const conditions = buildRuleCondition(draft, { duplicateRef, valueRef })
    if (!conditions) return
    const payload = {
      name: nameForKind(draft.kind, attribute),
      description: `Configured from ${entity.label} registration field ${attribute.display_name}.`,
      conditions,
      severity: draft.severity,
      message:
        draft.message.trim() ||
        defaultMessageForKind(draft.kind, attribute, {
          length: draft.length,
          pattern: draft.pattern,
        }),
      is_active: true,
      priority: 0,
      scope: 'entity_registration' as const,
      entity_type: entity.uuid,
      trigger_step: null,
    }

    try {
      if (editingRule) {
        await update.mutateAsync({ uuid: editingRule.uuid, ...payload })
        toast.success('Validation rule updated')
      } else {
        await create.mutateAsync(payload)
        toast.success('Validation rule added')
      }
      closeEditor()
    } catch (err) {
      toastApiError(err, 'Failed to save validation rule')
    }
  }

  async function deleteRule() {
    if (!deletingRule) return
    try {
      await remove.mutateAsync(deletingRule.uuid)
      toast.success('Validation rule deleted')
      const wasEditingDeleted = editingRuleUuid === deletingRule.uuid
      setDeletingRule(null)
      if (wasEditingDeleted) closeEditor()
    } catch (err) {
      toastApiError(err, 'Failed to delete validation rule')
    }
  }

  const canSave = (() => {
    if (!attribute) return false
    if (draft.kind === 'duplicate') return !!duplicateRef
    if (draft.kind === 'pattern') {
      return !!valueRef && isValidRegex(draft.pattern)
    }
    return !!valueRef && Number.isInteger(draft.length) && draft.length >= 1
  })()

  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Validation rules
          </p>
          <p className="mt-1 text-[11px] text-[var(--ink-3)]">
            Rules run as soon as the registration value is entered and can
            block, warn, or inform the user.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => openCreate()}
          disabled={busy}
          className="shrink-0"
        >
          <Plus size={13} />
          Add rule
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
          Loading validation rules...
        </div>
      ) : matchedRules.length === 0 ? (
        <p className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg)] px-3 py-3 text-[11px] text-[var(--ink-3)]">
          No validation rules configured for this field.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {matchedRules.map((entry) => {
            const Icon = iconForKind(entry.kind)
            return (
              <div
                key={entry.rule.uuid}
                className="flex items-center gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
                  <Icon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[12px] font-medium text-[var(--ink)]">
                      {labelForKind(entry.kind)}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {entry.rule.severity.toLowerCase()}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-[var(--ink-3)]">
                    {summaryForKind(entry.kind, {
                      length: entry.length,
                      pattern: entry.pattern,
                    })}
                    {entry.rule.message ? ` - ${entry.rule.message}` : ''}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => openEdit(entry)}
                  title="Edit validation rule"
                >
                  <Pencil size={13} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setDeletingRule(entry.rule)}
                  title="Delete validation rule"
                >
                  <Trash2 size={13} />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      <Dialog
        open={editorOpen}
        onOpenChange={(next) => {
          if (!next) closeEditor()
          else setEditorOpen(true)
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit validation rule' : 'Add validation rule'}
            </DialogTitle>
            <DialogDescription>
              Configures{' '}
              <span className="font-medium text-[var(--ink)]">
                {attribute.display_name}
              </span>
              . Rules fire as soon as the registration value is entered.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Rule type
              </Label>
              <Select
                value={draft.kind}
                onValueChange={(value: string) =>
                  changeKind(value as RegistrationRuleKind)
                }
                disabled={!!editingRule}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableKinds.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {labelForKind(kind)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Severity
              </Label>
              <Select
                value={draft.severity}
                onValueChange={(value: string) =>
                  setDraft((current) => ({ ...current, severity: value as Severity }))
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLOCK">Block</SelectItem>
                  <SelectItem value="WARN">Warn</SelectItem>
                  <SelectItem value="INFO">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {draft.kind === 'duplicate' ? (
              <div className="col-span-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2 text-[11px] text-[var(--ink-3)]">
                {attribute.field_type === 'ADDRESS' ? (
                  <>
                    Fires when another registration has the same address line,
                    postcode or city, and country when supplied.
                  </>
                ) : (
                  <>
                    Fires when the value already exists on another registration.
                    Uses <code className="font-mono">{duplicateRef}</code>.
                  </>
                )}
              </div>
            ) : null}

            {draft.kind === 'min_length' || draft.kind === 'max_length' ? (
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {draft.kind === 'min_length'
                    ? 'Minimum characters'
                    : 'Maximum characters'}
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={draft.length}
                  onChange={(event) =>
                    setDraft((current) => {
                      const parsed = Number.parseInt(event.target.value, 10)
                      const next = Number.isFinite(parsed)
                        ? Math.max(1, parsed)
                        : 1
                      const isDefaultMessage =
                        current.message ===
                        defaultMessageForKind(current.kind, attribute, {
                          length: current.length,
                        })
                      return {
                        ...current,
                        length: next,
                        message: isDefaultMessage
                          ? defaultMessageForKind(current.kind, attribute, {
                              length: next,
                            })
                          : current.message,
                      }
                    })
                  }
                />
              </div>
            ) : null}

            {draft.kind === 'pattern' ? (
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  Regex pattern
                </Label>
                <Input
                  value={draft.pattern}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      pattern: event.target.value,
                    }))
                  }
                  placeholder="e.g. ^SKU-[0-9]{4}$"
                  className="font-mono"
                />
                <p
                  className={cn(
                    'text-[10px]',
                    draft.pattern && !isValidRegex(draft.pattern)
                      ? 'text-[var(--status-error)]'
                      : 'text-[var(--ink-3)]',
                  )}
                >
                  {draft.pattern && !isValidRegex(draft.pattern)
                    ? 'Invalid regular expression.'
                    : 'Rule fires when the value does not match this regex.'}
                </p>
              </div>
            ) : null}

            <div className="col-span-2 flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Message
              </Label>
              <Input
                value={draft.message}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, message: event.target.value }))
                }
                placeholder="Shown when the rule matches"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeEditor}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveRule}
              disabled={busy || !canSave}
            >
              {busy
                ? 'Saving...'
                : editingRule
                  ? 'Save changes'
                  : 'Add rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={!!deletingRule}
        onOpenChange={(next) => {
          if (!next) setDeletingRule(null)
        }}
        title={`Delete ${deletingRule?.name ?? 'validation rule'}?`}
        description="This validation rule will stop running for future registrations."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={deleteRule}
      />
      <ValidationRuleDialog
        open={advancedOpen}
        onOpenChange={(next) => {
          setAdvancedOpen(next)
          if (!next) setAdvancedRule(null)
        }}
        rule={advancedRule}
        entity={entity}
        defaultScope="entity_registration"
      />
    </div>
  )
}

function buildComputedTemplateSuggestions(
  entity: EntityTypeDefinition,
  allEntities: EntityTypeDefinition[],
  currentAttributeUuid?: string,
): TemplateTokenSuggestion[] {
  const suggestions: TemplateTokenSuggestion[] = entity.attributes
    .filter((candidate) => candidate.uuid !== currentAttributeUuid)
    .map((candidate) => ({
      value: candidate.code_name,
      label: `${entity.label} · ${candidate.display_name}`,
      description: candidate.code_name,
    }))

  const entitiesByKey = new Map(
    allEntities.map((candidate) => [candidate.key, candidate]),
  )

  const addRelationshipSuggestions = (
    currentEntity: EntityTypeDefinition,
    relationshipPath: string[],
    labelPath: string[],
    visitedEntityKeys: Set<string>,
  ) => {
    if (relationshipPath.length >= COMPUTED_TEMPLATE_RELATIONSHIP_DEPTH) return

    for (const relationship of currentEntity.relationships) {
      const target = entitiesByKey.get(relationship.target_key)
      if (!target || visitedEntityKeys.has(target.key)) continue

      const nextRelationshipPath = [...relationshipPath, relationship.key]
      const nextLabelPath = [...labelPath, relationship.label]
      const tokenPrefix = nextRelationshipPath.join('.')
      const labelPrefix = nextLabelPath.join(' → ')

      for (const attribute of target.attributes) {
        if (attribute.uuid === currentAttributeUuid) continue
        suggestions.push({
          value: `${tokenPrefix}.${attribute.code_name}`,
          label: `${labelPrefix} · ${attribute.display_name}`,
          description: `${tokenPrefix}.${attribute.code_name}`,
        })
      }

      addRelationshipSuggestions(
        target,
        nextRelationshipPath,
        nextLabelPath,
        new Set([...visitedEntityKeys, target.key]),
      )
    }
  }

  addRelationshipSuggestions(entity, [], [], new Set([entity.key]))

  return suggestions
}

export function EntityAttributeDialog({
  entity,
  allEntities,
  attribute,
  open,
  onOpenChange,
  defaultOrder = 0,
  defaults,
  prefillName = '',
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
  attribute?: EntityAttributeDefinition
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultOrder?: number
  defaults?: AttributeDialogDefaults
  prefillName?: string
}) {
  const create = useCreateEntityAttribute(entity.uuid)
  const update = useUpdateEntityAttribute(entity.uuid)
  const { data: workflows = [] } = useWorkflowDefinitions()
  const isDisplayComputedCreate = !attribute && defaults?.source === 'COMPUTED'

  const workflowOptions = useMemo(
    () =>
      workflows.map((workflow) => ({
        value: workflow.key,
        label: `${workflow.label} (${workflow.key})`,
      })),
    [workflows],
  )

  const relationshipOptions = useMemo(
    () =>
      entity.relationships.map((relationship) => ({
        value: relationship.key,
        label: `${relationship.label} (${relationship.target_label})`,
      })),
    [entity.relationships],
  )

  const computedTemplateSuggestions = useMemo(
    () => buildComputedTemplateSuggestions(entity, allEntities, attribute?.uuid),
    [allEntities, attribute?.uuid, entity],
  )

  const initial = useMemo<EntityAttributeFormValues>(
    () => ({
      display_name: attribute?.display_name ?? prefillName,
      field_type: attribute?.field_type ?? 'TEXT',
      source: attribute?.source ?? defaults?.source ?? 'EAV',
      column_name: attribute?.column_name ?? '',
      option_values: optionValuesFromConfig(attribute?.configuration_data?.options),
      default_value: defaultValueFromConfig(
        attribute?.field_type ?? 'TEXT',
        attribute?.configuration_data?.default_value,
        optionValuesFromConfig(attribute?.configuration_data?.options),
      ),
      computed_default_template:
        typeof attribute?.configuration_data?.default_value === 'string'
          ? attribute.configuration_data.default_value
          : '',
      value_source: attribute?.configuration_data?.value_source ?? 'manual',
      workflow_key: attribute?.configuration_data?.workflow_key ?? '',
      relationship_key: attribute?.configuration_data?.relationship_key ?? '',
      editable: attribute?.editable ?? !isDisplayComputedCreate,
      required: attribute?.required ?? false,
      visible_when_enabled: parseVisibleWhen(attribute?.configuration_data).enabled,
      visible_when: parseVisibleWhen(attribute?.configuration_data).condition,
      required_when_visible: parseVisibleWhen(attribute?.configuration_data).requiredWhenVisible,
    }),
    [attribute, defaults?.source, isDisplayComputedCreate, prefillName],
  )

  const siblingSelectAttributes = useMemo(
    () =>
      (entity.attributes ?? []).filter(
        (a) =>
          a.uuid !== attribute?.uuid &&
          isChoiceAttributeFieldType(a.field_type),
      ),
    [entity.attributes, attribute?.uuid],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: 'Display name',
        value: initial.display_name,
        required: 'Display name is required',
        className: 'col-span-2',
      },
      {
        type: 'select',
        name: 'field_type',
        label: 'Field type',
        value: initial.field_type,
        options: FIELD_TYPE_OPTIONS,
        className: 'col-span-1',
        visible: () => !isDisplayComputedCreate,
      },
      {
        type: 'select',
        name: 'source',
        label: 'Storage source',
        value: initial.source,
        options: SOURCE_OPTIONS,
        className: 'col-span-1',
        visible: () => !isDisplayComputedCreate,
      },
      {
        type: 'select',
        name: 'value_source',
        label: 'Value source',
        value: initial.value_source,
        options: VALUE_SOURCE_OPTIONS,
        className: 'col-span-1',
        visible: () => !isDisplayComputedCreate,
      },
      {
        type: 'custom',
        name: 'computed_default_template',
        label: isDisplayComputedCreate ? 'Value template' : 'Default value template',
        value: initial.computed_default_template,
        className: 'col-span-2',
        visible: (formCtx) =>
          formCtx.values.source === 'COMPUTED' &&
          formCtx.values.value_source === 'manual',
        render: (formCtx) => (
          <ValueTemplateField
            label={
              isDisplayComputedCreate ? 'Value template' : 'Default value template'
            }
            description={
              isDisplayComputedCreate
                ? 'Type fixed text, or start typing an attribute name to insert a token. Suggestions appear when the typed text matches.'
                : 'Type a fixed default, or start typing an attribute name to insert a token. Suggestions appear when the typed text matches.'
            }
            value={String(formCtx.values.computed_default_template ?? '')}
            suggestions={computedTemplateSuggestions}
            onChange={(next) =>
              formCtx.setFieldValue('computed_default_template', next)
            }
            placeholder="e.g. Registration consumer.email"
          />
        ),
      },
      {
        type: 'text',
        name: 'column_name',
        label: 'Column name',
        placeholder: 'status',
        value: initial.column_name,
        className: 'col-span-1',
        visible: (formCtx) =>
          !isDisplayComputedCreate && formCtx.values.source === 'COLUMN',
      },
      {
        type: 'custom',
        name: 'option_values',
        label: 'Options',
        value: initial.option_values,
        className: 'col-span-2',
        visible: (formCtx) =>
          !isDisplayComputedCreate &&
          isChoiceAttributeFieldType(formCtx.values.field_type as string),
        render: (formCtx) => (
          <AttributeConfigurationField
            fieldType={String(formCtx.values.field_type ?? '')}
            options={(formCtx.values.option_values ?? []) as AttributeOption[]}
            onOptionsChange={(next) => formCtx.setFieldValue('option_values', next)}
            defaultValue={formCtx.values.default_value as AttributeDefaultValue}
            onDefaultValueChange={(next) => formCtx.setFieldValue('default_value', next)}
            showDefaultValue={
              !(
                formCtx.values.source === 'COMPUTED' &&
                formCtx.values.value_source === 'manual'
              )
            }
          />
        ),
      },
      {
        type: 'select',
        name: 'workflow_key',
        label: 'Workflow',
        placeholder: 'Select workflow',
        value: initial.workflow_key,
        options: workflowOptions,
        className: 'col-span-1',
        visible: (formCtx) =>
          !isDisplayComputedCreate && formCtx.values.value_source === 'workflow_state',
      },
      {
        type: 'select',
        name: 'relationship_key',
        label: 'Relationship',
        placeholder: 'Select relationship',
        value: initial.relationship_key,
        options: relationshipOptions,
        className: 'col-span-1',
        visible: (formCtx) =>
          !isDisplayComputedCreate &&
          formCtx.values.value_source === 'entity_relationship',
      },
      {
        type: 'checkbox',
        name: 'editable',
        label: 'Editable',
        value: initial.editable,
        className: 'col-span-1',
        visible: (formCtx) =>
          !isDisplayComputedCreate && formCtx.values.source !== 'COMPUTED',
      },
      {
        type: 'checkbox',
        name: 'required',
        label: 'Required',
        testId: 'entity-attr-required',
        value: initial.required,
        className: 'col-span-1',
        visible: (formCtx) =>
          !isDisplayComputedCreate &&
          formCtx.values.source !== 'COMPUTED' &&
          !PROJECTED_VALUE_SOURCES.has(String(formCtx.values.value_source ?? '')),
      },
      {
        type: 'custom',
        name: 'visible_when',
        label: '',
        value: initial.visible_when,
        className: 'hidden',
        render: () => null,
      },
      {
        type: 'custom',
        name: 'required_when_visible',
        label: '',
        value: initial.required_when_visible,
        className: 'hidden',
        render: () => null,
      },
      {
        type: 'custom',
        name: 'visible_when_enabled',
        label: 'Conditional visibility',
        value: initial.visible_when_enabled,
        className: 'col-span-2',
        visible: () => !isDisplayComputedCreate && siblingSelectAttributes.length > 0,
        render: (formCtx) => {
          const enabled = Boolean(formCtx.values.visible_when_enabled)
          const condition = (formCtx.values.visible_when ?? {
            field: '',
            values: [],
          }) as VisibleWhenCondition
          const selectedAttr = siblingSelectAttributes.find(
            (a) => a.code_name === condition.field,
          )
          const selectedAttrOptions: Array<{ label: string; value: string }> =
            selectedAttr?.configuration_data?.options ?? []

          return (
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    Conditional visibility
                  </p>
                  <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                    Only show this attribute when another field has a specific
                    value.
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={(checked) => {
                    formCtx.setFieldValue('visible_when_enabled', checked)
                    if (!checked) {
                      formCtx.setFieldValue('visible_when', {
                        field: '',
                        values: [],
                      })
                    }
                  }}
                />
              </div>
              {enabled && (
                <div className="space-y-3">
                  <div>
                    <Label className="mb-1.5 text-[11px] text-[var(--ink-3)]">
                      When this field
                    </Label>
                    <Select
                      value={condition.field}
                      onValueChange={(val) =>
                        formCtx.setFieldValue('visible_when', {
                          field: val,
                          values: [],
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a field…" />
                      </SelectTrigger>
                      <SelectContent>
                        {siblingSelectAttributes.map((a) => (
                          <SelectItem key={a.code_name} value={a.code_name}>
                            {a.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {condition.field && selectedAttrOptions.length > 0 && (
                    <div>
                      <Label className="mb-1.5 text-[11px] text-[var(--ink-3)]">
                        Has one of these values
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedAttrOptions.map((opt) => {
                          const selected = condition.values.includes(opt.value)
                          return (
                            <Badge
                              key={opt.value}
                              variant={selected ? 'default' : 'outline'}
                              className="cursor-pointer select-none"
                              onClick={() => {
                                const next = selected
                                  ? condition.values.filter(
                                      (v) => v !== opt.value,
                                    )
                                  : [...condition.values, opt.value]
                                formCtx.setFieldValue('visible_when', {
                                  ...condition,
                                  values: next,
                                })
                              }}
                            >
                              {opt.label}
                            </Badge>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {condition.field && condition.values.length > 0 && (
                    <Label className="flex cursor-pointer items-center gap-2.5 rounded-[6px] bg-[var(--bg)] px-3 py-2 font-normal">
                      <Checkbox
                        checked={Boolean(formCtx.values.required_when_visible)}
                        onCheckedChange={(checked) =>
                          formCtx.setFieldValue(
                            'required_when_visible',
                            Boolean(checked),
                          )
                        }
                      />
                      <span className="text-[12px] text-[var(--ink-2)]">
                        Required when visible
                      </span>
                    </Label>
                  )}
                </div>
              )}
            </div>
          )
        },
      },
    ],
    [
      computedTemplateSuggestions,
      initial,
      isDisplayComputedCreate,
      relationshipOptions,
      siblingSelectAttributes,
      workflowOptions,
    ],
  )

  const { Form, ref } = useForm(
    {
      name: `entity-attribute-${entity.key}-${attribute?.uuid ?? 'new'}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = {
        ...initial,
        ...(rawValues as Partial<EntityAttributeFormValues>),
      } as EntityAttributeFormValues
      const previousConfig = attribute?.configuration_data ?? {}
      const selectOptions = isChoiceAttributeFieldType(values.field_type)
        ? optionsToPayload(values.option_values)
        : []
      if (isChoiceAttributeFieldType(values.field_type) && selectOptions.length === 0) {
        toast.error('Select fields need at least one option')
        return
      }
      const configuration_data = {
        ...previousConfig,
        value_source: values.value_source,
        ...(selectOptions.length > 0 ? { options: selectOptions } : {}),
        workflow_key:
          values.value_source === 'workflow_state' ? values.workflow_key : '',
        relationship_key:
          values.value_source === 'entity_relationship' ? values.relationship_key : '',
      }
      const vw = values.visible_when as VisibleWhenCondition | undefined
      if (values.visible_when_enabled && vw?.field && vw.values.length > 0) {
        configuration_data.visible_when = { [vw.field]: vw.values }
        if (values.required_when_visible) {
          configuration_data.required_when = { [vw.field]: vw.values }
        } else {
          delete configuration_data.required_when
        }
      } else {
        delete configuration_data.visible_when
        delete configuration_data.required_when
      }
      if (!isChoiceAttributeFieldType(values.field_type)) {
        delete configuration_data.options
      }
      const usesComputedManualTemplate =
        values.source === 'COMPUTED' && values.value_source === 'manual'
      if (usesComputedManualTemplate) {
        const template = values.computed_default_template.trim()
        if (template) {
          configuration_data.default_value = template
        } else {
          delete configuration_data.default_value
        }
      } else {
        const defaultValue = normaliseDefaultValue(
          values.field_type,
          values.default_value,
          selectOptions,
        )
        if (hasDefaultValue(defaultValue)) {
          configuration_data.default_value = defaultValue
        } else {
          delete configuration_data.default_value
        }
      }
      const payload = {
        display_name: values.display_name.trim(),
        code_name:
          attribute?.code_name ??
          toCodeName(values.display_name),
        description: attribute?.description ?? '',
        field_type: values.field_type,
        source: values.source,
        column_name: values.source === 'COLUMN' ? values.column_name.trim() : '',
        required: PROJECTED_VALUE_SOURCES.has(String(values.value_source ?? ''))
          ? (attribute?.required ?? false)
          : Boolean(values.required),
        editable: values.source === 'COMPUTED' ? false : Boolean(values.editable),
        order: attribute?.order ?? defaultOrder,
        configuration_data,
      }

      try {
        if (attribute) {
          await update.mutateAsync({ attrUuid: attribute.uuid, ...payload })
          toast.success('Entity attribute updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Entity attribute created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to save entity attribute')
      }
    },
    [open, fields, attribute?.uuid],
  )

  const busy = create.isPending || update.isPending

  // Hard-reset form on open: switching which attribute you edit, or opening
  // for a new prefillName, would otherwise show the previous state.
  useEffect(() => {
    if (!open) return
    ref.current?.resetForm({ values: initial as unknown as Record<string, unknown> })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attribute?.uuid, prefillName])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          ref.current?.resetForm()
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className={isDisplayComputedCreate ? 'sm:max-w-[460px]' : 'sm:max-w-[720px]'}>
        <DialogHeader>
          <DialogTitle>
            {attribute
              ? 'Edit Attribute'
              : isDisplayComputedCreate
                ? 'New computed display field'
                : 'New Attribute'}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => ref.current?.submitForm()} disabled={busy}>
            {busy ? 'Saving…' : attribute ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Entity Actions Tab
// ---------------------------------------------------------------------------

const ACTION_KIND_LABELS: Record<ActionKind, string> = {
  command: 'Command',
  workflow_transition: 'Workflow Transition',
  edit_fields: 'Edit Fields',
  navigate: 'Navigate',
}

export function EntityActionsTab({
  entityUuid,
  entityKey,
  attributes = [],
  relationships = [],
}: {
  entityUuid: string
  entityKey: string
  attributes?: EntityAttributeDefinition[]
  relationships?: EntityRelationshipDefinition[]
}) {
  const { data: actions = [], isLoading } = useEntityActions(entityUuid)
  const reorder = useReorderEntityActions(entityUuid)
  const remove = useDeleteEntityAction(entityUuid)
  const [editing, setEditing] = useState<EntityAction | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [deletingAction, setDeletingAction] = useState<EntityAction | null>(null)

  const sorted = useMemo(
    () => [...actions].sort((a, b) => a.sort_order - b.sort_order),
    [actions],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = sorted.findIndex((a) => a.uuid === active.id)
      const newIndex = sorted.findIndex((a) => a.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = arrayMove(sorted, oldIndex, newIndex)
      reorder.mutate(
        reordered.map((a, i) => ({ uuid: a.uuid, sort_order: i })),
      )
    },
    [sorted, reorder],
  )

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Actions
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Configurable actions available on {entityKey} resource detail pages.
          </p>
        </div>
        <Button variant="outline" size="xs" onClick={() => setShowCreate(true)}>
          <Plus size={11} className="mr-1" />
          Add action
        </Button>
      </div>

      <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
        {isLoading ? (
          <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
            Loading actions…
          </div>
        ) : sorted.length === 0 ? (
          <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
            No actions configured yet.
          </div>
        ) : (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={sorted.map((a) => a.uuid)}
              strategy={verticalListSortingStrategy}
            >
              {sorted.map((action, idx) => (
                <SortableEntityActionRow
                  key={action.uuid}
                  action={action}
                  isFirst={idx === 0}
                  onEdit={() => setEditing(action)}
                  onDelete={() => setDeletingAction(action)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <EntityActionDialog
        entityUuid={entityUuid}
        entityKey={entityKey}
        action={undefined}
        open={showCreate}
        onOpenChange={setShowCreate}
        attributes={attributes}
        relationships={relationships}
      />

      {editing && (
        <EntityActionDialog
          entityUuid={entityUuid}
          entityKey={entityKey}
          action={editing}
          open={!!editing}
          onOpenChange={(next) => {
            if (!next) setEditing(null)
          }}
          attributes={attributes}
          relationships={relationships}
        />
      )}

      <ConfirmationDialog
        open={!!deletingAction}
        onOpenChange={(next) => {
          if (!next) setDeletingAction(null)
        }}
        title="Delete action"
        description={`Permanently delete "${deletingAction?.label}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={remove.isPending}
        onConfirm={async () => {
          if (!deletingAction) return
          await remove.mutateAsync(deletingAction.uuid)
          setDeletingAction(null)
        }}
      />
    </section>
  )
}

function SortableEntityActionRow(props: {
  action: EntityAction
  isFirst: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.action.uuid })

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className={cn(!props.isFirst && 'border-t-[0.5px] border-t-[var(--rule)]')}
    >
      <EntityActionRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

function EntityActionRow({
  action,
  onEdit,
  onDelete,
  dragHandleProps,
}: {
  action: EntityAction
  isFirst?: boolean
  onEdit: () => void
  onDelete: () => void
  dragHandleProps?: Record<string, unknown>
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 bg-[var(--bg)] px-3 py-2',
        !action.is_active && 'opacity-50',
      )}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-[var(--ink-3)] hover:text-[var(--ink-2)]"
        {...dragHandleProps}
      >
        <GripVertical size={14} />
      </button>

      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[var(--ink-2)]">
        {action.icon_name ? (
          <LucideIconByName name={action.icon_name} size={14} />
        ) : (
          <span className="h-3 w-3 rounded-sm bg-[var(--ink-3)]/20" />
        )}
      </span>

      <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--ink-1)]">
        {action.label}
      </span>

      <Badge variant="secondary" className="shrink-0 text-[10px]">
        {ACTION_KIND_LABELS[action.action_kind] ?? action.action_kind}
      </Badge>

      {action.variant === 'destructive' && (
        <Badge variant="destructive" className="shrink-0 text-[10px]">
          Destructive
        </Badge>
      )}

      {!action.is_active && (
        <Badge variant="outline" className="shrink-0 text-[10px]">
          Disabled
        </Badge>
      )}

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-1)]"
        >
          <Pencil size={12} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--ink-3)] hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/20"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Entity Action Dialog (create / edit)
// ---------------------------------------------------------------------------

const ACTION_KIND_OPTIONS: { value: ActionKind; label: string }[] = [
  { value: 'command', label: 'Command' },
  { value: 'workflow_transition', label: 'Workflow Transition' },
  { value: 'edit_fields', label: 'Edit Fields' },
  { value: 'navigate', label: 'Navigate' },
]

const ACTION_VARIANT_OPTIONS: { value: ActionVariant; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'destructive', label: 'Destructive' },
]

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
}

export function EntityActionDialog({
  entityUuid,
  entityKey,
  action,
  open,
  onOpenChange,
  attributes = [],
  relationships = [],
}: {
  entityUuid: string
  entityKey?: string
  action: EntityAction | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  attributes?: EntityAttributeDefinition[]
  relationships?: EntityRelationshipDefinition[]
}) {
  const create = useCreateEntityAction(entityUuid)
  const update = useUpdateEntityAction(entityUuid)
  const { data: handlers = [] } = useEntityActionHandlers()

  const [label, setLabel] = useState(action?.label ?? '')
  const [key, setKey] = useState(action?.key ?? '')
  const [iconName, setIconName] = useState(action?.icon_name ?? '')
  const [variant, setVariant] = useState<ActionVariant>(action?.variant ?? 'default')
  const [actionKind, setActionKind] = useState<ActionKind>(action?.action_kind ?? 'command')
  const [handlerKey, setHandlerKey] = useState(action?.handler_key ?? '')
  const [permissionKey, setPermissionKey] = useState(action?.permission_key ?? '')
  const [isActive, setIsActive] = useState(action?.is_active ?? true)
  const [confirmMessage, setConfirmMessage] = useState(action?.confirmation?.message ?? '')
  const [requireReason, setRequireReason] = useState(
    action?.confirmation?.require_reason ?? false,
  )
  const [captureFields, setCaptureFields] = useState<string[]>(
    action?.capture_fields ?? [],
  )
  const [availability, setAvailability] = useState<PredicateNode | null>(
    (action?.availability && Object.keys(action.availability).length > 0
      ? action.availability
      : null) as PredicateNode | null,
  )

  const busy = create.isPending || update.isPending
  const isEdit = !!action

  const handleLabelChange = (val: string) => {
    setLabel(val)
    if (!isEdit) {
      setKey(slugify(val))
    }
  }

  // Attributes not yet selected as capture fields
  const availableAttributes = useMemo(
    () => attributes.filter((a) => !captureFields.includes(a.code_name)),
    [attributes, captureFields],
  )

  const addCaptureField = (codeName: string) => {
    setCaptureFields((prev) => [...prev, codeName])
  }

  const removeCaptureField = (codeName: string) => {
    setCaptureFields((prev) => prev.filter((f) => f !== codeName))
  }

  const handleSave = async () => {
    const payload: Record<string, unknown> = {
      label,
      key: isEdit ? key : slugify(label),
      icon_name: iconName,
      variant,
      action_kind: actionKind,
      handler_key: handlerKey,
      permission_key: permissionKey,
      is_active: isActive,
      capture_fields: captureFields,
      confirmation: {
        message: confirmMessage || undefined,
        require_reason: requireReason || undefined,
      },
      availability: availability ?? {},
    }

    try {
      if (isEdit) {
        await update.mutateAsync({ actionUuid: action.uuid, ...payload } as Parameters<typeof update.mutateAsync>[0])
      } else {
        await create.mutateAsync(payload as Parameters<typeof create.mutateAsync>[0])
      }
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, `Failed to ${isEdit ? 'update' : 'create'} action`)
    }
  }

  const { refs, byRef: lookupRef } = useAvailableRefs({ entityKey: entityKey })

  // Build a summary of what context the availability conditions can see
  const contextSummary = useMemo(() => {
    const items: string[] = []
    if (entityKey) items.push(`${entityKey} fields`)
    for (const rel of relationships) {
      items.push(`${rel.target_label ?? rel.target_key} (${rel.cardinality === 'ONE' ? '1:1' : '1:N'})`)
    }
    // Workflow context is always available via entity:*.current_state refs
    items.push('workflow state')
    return items
  }, [entityKey, relationships])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Action' : 'New Action'}</DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--ink-2)]">
            {isEdit
              ? 'Modify the action configuration.'
              : 'Create a new action for this entity type.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {/* ── Basics ── */}
          <section className="flex flex-col gap-3">
            <h4 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
              Basics
            </h4>
            <div className="grid grid-cols-[1fr_auto] items-end gap-3">
              <div className="flex flex-col gap-1">
                <Label>Label</Label>
                <Input
                  value={label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="e.g. Cancel Warranty"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Icon</Label>
                <LucideIconPicker value={iconName} onChange={setIconName} mode="floating" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Label>Variant</Label>
                <Select value={variant} onValueChange={(v) => setVariant(v as ActionVariant)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_VARIANT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Active</Label>
                <div className="flex h-9 items-center">
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                </div>
              </div>
            </div>
          </section>

          {/* ── Behaviour ── */}
          <section className="flex flex-col gap-3">
            <h4 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
              Behaviour
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Label>Action kind</Label>
                <Select
                  value={actionKind}
                  onValueChange={(v) => setActionKind(v as ActionKind)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_KIND_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(actionKind === 'command' || actionKind === 'workflow_transition') && (
                <div className="flex flex-col gap-1">
                  <Label>Handler</Label>
                  <Select value={handlerKey} onValueChange={setHandlerKey}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select handler" />
                    </SelectTrigger>
                    <SelectContent>
                      {handlers.map((h) => (
                        <SelectItem key={h.key} value={h.key}>
                          {h.key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label>Permission key (optional)</Label>
              <Input
                value={permissionKey}
                onChange={(e) => setPermissionKey(e.target.value)}
                placeholder="e.g. warranties.cancel"
              />
            </div>
          </section>

          {/* ── Capture fields (for edit_fields kind) ── */}
          {actionKind === 'edit_fields' && (
            <section className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] p-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
                  Capture fields
                </h4>
                <span className="text-[10px] text-[var(--ink-3)]">
                  {captureFields.length} field{captureFields.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-[var(--ink-3)]">
                Select which entity attributes the user must fill in when executing this action.
                The form is auto-generated from the attribute field types.
              </p>

              {/* Selected fields */}
              {captureFields.length > 0 && (
                <div className="flex flex-col gap-1">
                  {captureFields.map((codeName) => {
                    const attr = attributes.find((a) => a.code_name === codeName)
                    return (
                      <div
                        key={codeName}
                        className="flex items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-2.5 py-1.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[12px] text-[var(--ink)]">
                            {attr?.display_name ?? codeName}
                          </span>
                          <span className="font-mono text-[10px] text-[var(--ink-3)]">
                            {codeName}
                          </span>
                          {attr && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0">
                              {attr.field_type}
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => removeCaptureField(codeName)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Add field dropdown */}
              {availableAttributes.length > 0 && (
                <Select
                  value=""
                  onValueChange={(v) => { if (v) addCaptureField(v) }}
                >
                  <SelectTrigger className="text-[12px]">
                    <SelectValue placeholder="Add a field…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAttributes.map((attr) => (
                      <SelectItem key={attr.code_name} value={attr.code_name}>
                        <span className="flex items-center gap-2">
                          <span>{attr.display_name}</span>
                          <span className="font-mono text-[10px] text-[var(--ink-3)]">
                            {attr.field_type}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {attributes.length === 0 && (
                <p className="text-[11px] text-[var(--ink-3)]">
                  No attributes defined on this entity type yet.
                </p>
              )}
            </section>
          )}

          {/* ── Confirmation ── */}
          <section className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] p-3">
            <h4 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
              Confirmation
            </h4>
            <div className="flex flex-col gap-1">
              <Label>Message (leave blank to skip confirmation)</Label>
              <Input
                value={confirmMessage}
                onChange={(e) => setConfirmMessage(e.target.value)}
                placeholder="Are you sure you want to…"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={requireReason} onCheckedChange={(v) => setRequireReason(!!v)} />
              <Label className="cursor-pointer">Require reason</Label>
            </div>
          </section>

          {/* ── Availability conditions ── */}
          <section className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] p-3">
            <h4 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
              Availability conditions
            </h4>
            <p className="text-[11px] leading-relaxed text-[var(--ink-3)]">
              Control when this action is visible. Conditions have access to: {contextSummary.join(', ')}.
              Leave empty to always show.
            </p>
            <ConditionBuilder
              value={availability}
              onChange={setAvailability}
              availableRefs={refs}
              byRef={lookupRef}
            />
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={busy || !label.trim()}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
